import { VENDOR_BASE_API_URL } from './constants'
import { CENTER_OF_EXCELLENCE } from '../../centerOfExcellence/types'

// When naming new mixpanel events please follow the best practices: https://lyrahealth.atlassian.net/wiki/spaces/DS/pages/2598110057/Mixpanel+event+naming+best+practices
export enum COMMON_BCX_SCREENS {
  GUIDE = 'guide',
  ACTIVITIES_LIST = 'activities list',
  LESSON_OVERVIEW = 'lesson overview',
  LESSON_VIDEO_PLAYER = 'lesson video player',
  VIDEO_PLAYER = 'video player',
  LESSON_REVIEW = 'lesson review',
  EXERCISE_OVERVIEW = 'exercise overview',
  PASSIVE_SI_MESSAGE = 'passive SI message',
  ACTIVE_SI_MESSAGE = 'active SI message',
  HOMEBASE = 'homebase',
  PROGRESS = 'progress tab',
  SUMMARY_PAGE = 'summary page',
  GRADUATION_PAGE = 'graduation page',
  ESSENTIALS = 'essentials',
  VIDEO_SESSION_COMPLETED = 'video session completed',
  COMPLEX_CARE_LANDING_PAGE = 'complex care landing page',
  COMPLEX_CARE_SUB_PAGE = 'complex care programs subpage',
}

export enum COMMON_REGISTRATION_SCREENS {
  REGISTRATION_FORM_DEFAULT = 'registration form - default',
  REGISTRATION_FORM_BACKGROUND_INFO = 'registration form - background info',
  REGISTRATION_FORM_CARE_TEAM = 'registration form - care team',
  REGISTRATION_FORM_HPI_EMAIL = 'registration form - HPI email',
  REGISTRATION_FORM_PARENT_LED_TEEN = 'registration form - parent-led lct teen booking',
  REGISTRATION_FORM_AUTO_ENROLLMENT = 'registration form - auto enrollment',
  REGISTRATION_FORM_PROVIDER_LED = 'registration form - provider-led',
  REGISTRATION_FORM_DBT_REFERRAL = 'registration form - dbt referral',
}

export enum COMMON_ELIGIBILITY_SCREENS {
  CONFIRM_ELIGIBILITY_FORM = 'confirm eligibility form',
}

export type MIXPANEL_EVENT =
  | COMMON_MIXPANEL_EVENTS
  | (typeof BCX_EVENTS)[keyof typeof BCX_EVENTS]
  | (typeof S4C_EVENTS)[keyof typeof S4C_EVENTS]
  | (typeof REGISTRATION_EVENTS)[keyof typeof REGISTRATION_EVENTS]
  | (typeof ELIGIBILITY_EVENTS)[keyof typeof ELIGIBILITY_EVENTS]
  | TRIAGE_EVENTS
  | LATEST_PROVIDER_EVENTS
  | LOGIN_EVENTS
  | PATHWAYS_EVENTS

export enum COMMON_MIXPANEL_EVENTS {
  BUTTON_PRESS = 'Button Press',
  ESSENTIALS_ERROR = 'view essentials error',
  COMPLEX_CARE_ERROR = 'view complex care error',
  ELECTION_ERROR = 'view election error',
  FILTER_CHANGE = 'Filter Change',
  GENERIC_ERROR = 'view generic error',
  PAGE_LOAD = 'Page Load',
  CHAT_COUNTRY_DETECTION_FAILURE = 'Chat country detection failure',
  CLICK = 'click',
  CLICK_LYRA_HEALTH_APP = 'click lyra health app',
  CLICK_EXTERNAL_RESOURCE = 'click external resource',
  CLICK_SCHEDULE_A_CN_CALL = 'click schedule a cn call',
  CLICK_PHONE_CN = 'click cn phone contact',
  CLICK_EMAIL_CN = 'click email cn',
  ERROR_BANNER = 'view error banner',
  CLICK_HAMBURGER_MENU = 'click hamburger menu',
  CLICK_MY_BENEFITS = 'click my benefits',
  CLICK_ACCOUNT = 'click account',
  CLICK_FAQS = 'click FAQs',
  CLICK_LOGOUT = 'click logout',
  CLICK_LYRA_LOGO = 'click lyra logo',
  CLICK_HEADER_LYRA_LOGO = 'click header lyra logo',
}

export enum WELLNESS_GOALS_PROPERTIES {
  SELECTED_GOALS = 'selected_goals',
  CURRENT_GOALS = 'current_goals',
  TOOLKIT_TOPICS_SHOWN = 'toolkit_topics_shown',
  TOOLKIT_TOPICS_SHOWN_RANKS = 'toolkit_topics_shown_ranks',
}

export enum HOMEBASE_ESSENTIALS_CAROUSEL_CONTENT_PROPERTIES {
  ARTICLE = 'article',
  COURSE = 'course',
  ESSENTIALS_CAROUSEL_CONTENT = 'essentials_carousel_content',
}

export enum ESSENTIALS_CONTENT_ITEM_PROPERTIES {
  ITEM_ID = 'item_id',
  ITEM_TYPE = 'item_type',
  ITEM_RANK = 'item_rank',
  ITEM_CLICKED = 'item_clicked',
}

export type MIXPANEL_PROPERTY =
  | COMMON_MIXPANEL_PROPERTIES
  | S4C_PROPERTIES
  | ELIGIBILITY_PROPERTIES
  | LATEST_PROVIDER_PROPERTIES
  | INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES
  | HEALTHCARE_BOOKING_PROPERTIES
  | ESSENTIALS_CONTENT_EVENTS_PROPERTIES
  | PROVIDER_VIDEO_PROPERTIES
  | PATHWAYS_PROPERTIES
  | ZOOM_PROPERTIES
  | ERROR_BANNER_PROPERTIES
  | URGENCY_TO_BOOK_PROPERTIES
  | FORKED_TRIAGE_PROPERTIES
  | WELLNESS_TOOLKIT_EVENTS_PROPERTIES
  | WELLNESS_CHECK_IN_PROPERTIES
  | ESSENTIALS_LIVE_EVENTS_PROPERTIES
  | WELLNESS_GOALS_PROPERTIES
  | TRAUMA_CHECKIN_PROPERTIES
  | TOOLKIT_PROPERTIES
  | DBT_HOMEBASE_PROPERTIES
  | DBT_TEEN_PROPERTIES
  | REGISTRATION_PROPERTIES

export enum COMMON_MIXPANEL_PROPERTIES {
  REGISTERED_COUNTRY = 'registered_country',
  GUIDE_NAME = 'guideName',
  ASSIGNMENT_ID = 'assignmentId',
  PROGRAM = 'program',
  ASSESSMENT_NAME = 'assessmentName',
  ASSESSMENT_TYPE = 'assessmentType',
  EXERCISE_NAME = 'exerciseName',
  PERCENT_PROGRESS = 'percentProgress',
  LESSON_NAME = 'lessonName',
  LESSON_STATUS = 'lessonStatus',
  SCREEN = 'screen',
  PREVIOUS_SCREEN = 'previousScreen',
  PAGE = 'page',
  ACTION = 'action',
  CTA_CLICKED = 'cta_clicked',
  CUSTOMER_ID = 'customer_id',
  CUSTOMER_NAME = 'customer_name',
  DETAILS = 'details',
  ERROR_TYPE = 'error_type',
  ERROR_DETAIL = 'error_detail',
  STATUS = 'status',
  SELECTED_COUNTRY = 'selectedCountry',
  ENTRY_POINT = 'entryPoint',
  WORKLIFE_SERVICE = 'worklife_service',
  APP_ID = 'app_id',
  APP_PLATFORM = 'app_platform',
  LYRA_ID = 'lyra_id',
  CUSTOMER_CONFIGURATION = 'customer_configuration',
  COUNTRY_FLAG = 'country_flag',
  USER_TIMEZONE = 'user_timezone',
  MESSAGE_LOCATION = 'message_location',
  RESOURCE_NAME = 'resource_name',
  BUTTON_TEXT = 'button_text',
  COUNTRY_CATEGORY = 'country_category',
  ATTEMPT_COUNT = 'attempt_count',
  PARTNER_NAME = 'partner_name',
  EVENT = 'event',
  TRIGGER = 'trigger',
  REFERRER_PAGE = 'referrer_page',
  TEXT_INPUT = 'text_input',
  PREFERRED_NAME = 'preferred_name',
  VALUES = 'values',
  ERRORS = 'errors',
  BANNER = 'banner',
  // Used for experiments
  ARM = 'arm',
  NOTIFICATION_TYPE = 'notification_type',
  NOTIFICATION_BADGE_SHOWN = 'notification_badge_shown',

  // on LW USER ID is automatically added
  USER_ID = ' User ID',
  LD_DEVICE_UUID = 'ld_device_uuid',
  TAB_ID = 'tab_id',
  REENGAGEMENT_RANK = 'reengagement_rank',
  CARE_REFERENCE_ID = 'care_reference_id', // id used to track the user that may be coming from care.lyrahealth.com
  CUSTOMER_PRODUCT_TYPE = 'customer_product_type',
  BENEFIT_PARTNER = 'benefit_partner',
  PERSONIFY_ACTIVATION = 'personify_activation',
  HEALTH_PLAN_NAME = 'health_plan_name',
  DESTINATION_URL = 'destination_url',
  UTM_MEDIUM = 'utm_medium',
  UTM_CAMPAIGN = 'utm_campaign',
  UTM_SOURCE = 'utm_source',
  UTM_CONTENT = 'utm_content',
  VERSION = 'version',
}

export enum COMMON_ENTRY_POINTS {
  EMAIL = 'email',
  HAMBURGER_MENU = 'hamburger menu',
  SESSION_LIMIT_EMAIL = 'session limit email',
  SELF_PAY_TRANSITION_EMAIL = 'self-pay transition email',
  INELIGIBLE_HEALTH_PLAN = 'ineligible health plan',
  HOMEBASE_CARE_CAROUSEL = 'homebase care carousel',
  SMART_SEARCH_RESULTS = 'smart search results',
  SMART_SEARCH_SHORTCUT = 'smart search shortcut',
}

/** Shared event strings used across multiple event groups */
const SHARED_HIGH_RISK_EVENTS = {
  /** Tracks when user clicks to navigate to direct path from any high-risk flow (direct booking modal or custom preferences redirect) */
  CLICK_DIRECT_PATH: 'click direct path',
}

export const DIRECT_BOOKING_HIGH_RISK_MODAL_EVENTS = {
  VIEW_DIRECT_BOOKING_HIGH_RISK_MODAL: 'view direct booking high risk modal',
  CLICK_DIRECT_PATH: SHARED_HIGH_RISK_EVENTS.CLICK_DIRECT_PATH,
  CLICK_TO_CONTACT_CN: 'click to contact cn',
}

export const CUSTOM_PREFERENCES_EVENTS = {
  SUBMIT_PREFERENCES_FREE_TEXT: 'submit preferences free text',
  VIEW_PREFERENCES_FREE_TEXT_VALIDATION_ERROR: 'view free text input validation error',
  VIEW_HIGH_RISK_MODAL: 'view high risk modal',
  CLICK_DIRECT_PATH: SHARED_HIGH_RISK_EVENTS.CLICK_DIRECT_PATH,
  CLICK_REMOVE_PROVIDER_PREFERENCE: 'click remove provider preference',
  VIEW_PREFERENCES_FREE_TEXT: 'view preferences free text',
  VIEW_PREFERENCES_FREE_TEXT_PLAYBACK: 'view preferences free text playback',
}

export enum DIRECT_BOOKING_HIGH_RISK_MODAL_PROPERTIES {
  DISABLED_BOOKING = 'disabled_booking',
  DISABLED_BOOKING_REASON = 'disabled_booking_reason',
}

export enum DISABLED_BOOKING_REASON {
  HIGH_RISK = 'high risk',
}

export enum ERROR_BANNER_PROPERTIES {
  ENDPOINT_URL = 'endpoint_url',
  ENDPOINT_METHOD = 'endpoint_method',
  HTTP_ERROR_CODE = 'http_error_code',
  ERROR_MESSAGE = 'api_error_message',
  LYRA_ERROR_MESSAGE = 'lyra_error_message',
}

export enum INTEGRATED_BENEFITS_MIXPANEL_PROPERTIES {
  BENEFITS = 'benefits',
  MATCH_FOR = 'match_for',
  TAB_DISPLAY_NAME = 'tab_display_name',
  ADDITIONAL_BENEFITS = 'has_additional_benefits',
  EXPLORE_ALL_BENEFITS = 'has_explore_all_benefits',
  PREMIUM_NEURO_BANNER = 'has_premium_neuro_banner',
}

export const CARELON_MIXPANEL_OTHER_TREATMENT = 'Carelon behavioral health'

export const CARELON_MIXPANEL_BENEFITS = [
  {
    category: 'selfCare',
    isRecommended: false,
    vendor: 'Carelon Behavioral Health',
  },
] as const

export enum INTEGRATED_BENEFITS_TAB_MIXPANEL_VALUES {
  PICKED_FOR_YOU = 'Pick for you',
  MORE_BENEFITS = 'More benefits',
}

export enum S4C_PROPERTIES {
  PRIMARY_RECOMMENDATIONS = 'primary_recommedations',
  SECONDARY_RECOMMENDATIONS = 'secondary_recommedations',
  OTHER_TREATMENTS = 'other_treatments',
  TREATMENT_DETAIL = 'treatment_detail',
  CHILD_LYRA_ID = 'child_lyra_id',
  CHILD_LYRA_IDS = 'child_lyra_ids',
  MATCH_FOR = 'match_for',
  HEALTH_PLAN_NAME = 'health_plan_name',
  MATCHER_REQUEST_ID = 'matcher_request_id',
  SEARCH_ID = 'search_id',
  TREATMENT_OPTION = 'treatmentOption',
  TREATMENT_OPTION_DETAIL = 'treatmentOption_detail',
  TREATMENT_OPTION_SHOWN = 'treatment_option_shown',
  IS_PRIMARY_RECOMMENDATION = 'is_primary_recommendation',
  CTA_ENTRYPOINT = 'CTA_entrypoint',
  MEETING_FORMAT = 'meetingFormat',
  CLIENT_TIER_LEVEL = 'client_tier_level',
  CLINICAL_TIER = 'clinicalTier',
  CLINICAL_TIER_WITH_EXCLUSIONS = 'clinicalTierWithExclusions',
  PAGE_LAYOUT = 'pageLayout',
  RECOMMENDED_PROGRAMS = 'recommendedPrograms',
  ASSERTIVE_TRIAGE = 'assertive_triage',
  PROVIDER_ID = 'providerID',
  CALENDAR_SLOT_CLICKED = 'calendar_slot_clicked',
  NUM_PROVIDERS_SHOWN = 'numProvidersShown',
  ONSITE = 'onsite',
  PROVIDERS_SHOWN = 'providersShown',
  NUM_CALENDAR_SLOTS = 'num_calendar_slots',
  CALENDAR_SLOTS_SHOWN = 'calendar_slots_shown',
  DIRECT_LINK = 'directLink',
  DIRECT_ACCESS_OFFERED = 'direct_access_offered',
  SESSION_COST = 'session_cost',
  OTHER_NEED = 'otherNeed',
  PRIMARY_NEED = 'primaryNeed',
  HOURS_TO_INITIAL_SESSION = 'hoursToInitialSession',
  PROVIDER_TYPE = 'providerType',
  PREFERENCE_CATEGORY = 'preference_category',
  PREFERENCE_CATEGORIES = 'preference_categories',
  PREFERENCE_DETAIL = 'preference_detail',
  PREFERENCES_SHOWN = 'preferences_shown',
  CN_entrypoint = 'CN_entrypoint',
  ENTRYPOINT_DETAIL = 'entrypoint_detail',
  RESULTS_TYPE = 'results_type',
  CONTACT_OPTIONS = 'contact_options',
  CHILD_CONCERN_TYPE = 'concern_type',
  HAS_PREVIOUS_SEARCH = 'has_previous_search',
  REQUEST_TYPE = 'request_type',
  LOCATION_INPUT = 'location_input',
  IN_PERSON_PROVIDERS_SHOWN = 'in_person_providers_shown',
  IS_DIRECT_PATH = 'is_direct_path',
  TREATMENT_OPTION_CTPO = 'treatment_option_ctpo',
  HAS_CONNECTED_CALENDAR = 'has_connected_calendar',
  PROVIDER_RANK = 'provider_rank',
  DAY_OF_WEEK = 'day_of_week',
  THERAPY_MODALITY = 'therapyModality',
  DIRECT_LINK_INTENT = 'direct_link_intent',
  DIRECT_LINK_SOURCE = 'direct_link_source',
  SHOWN_CLINICAL_CONDITIONS = 'shown_clinical_conditions',
  AGE = 'age',
  GUARDIAN_AGE = 'guardian_age',
  TESTIMONIAL_INDEX = 'testimonial_index',
  SELECTED_MODALITY = 'selected_modality',
  CONTACT_INFO_CHECKBOX = 'contact_info_checkbox',
  AI_BIO = 'AI bio',
  PROVIDER_DETAIL = 'provider_detail',
  SMS_CONSENT = 'sms_consent',
  PHONE_TYPE = 'phone_type',
  SURVEY_RESPONSE = 'survey_response',
  PROVIDER_AVAILABLE_SLOTS = 'provider_available_slots',
  AVAILABILITY_PREFERENCE_MATCH = 'availability_preference_match',
  MATCHED_CALENDAR_SLOTS_SHOWN = 'matched_calendar_slots_shown',
  SHOWN_INPRODUCT_SURVEY = 'shown_inproduct_survey',
  TOOLKIT_TOPICS_SHOWN = 'toolkit_topics_shown',
  TOOLKIT_TOPICS_SHOWN_RANKS = 'toolkit_topics_shown_ranks',
  HAS_STARTED_SEARCH_FOR_SELF = 'has_started_search_for_self',
  PREFERENCES_ENTRYPOINT_VISIBLE = 'preferences_entrypoint_visible',
  PREFERENCES_ENTRYPOINT = 'preferences_entrypoint',
  AVAILABILITY_EXPANDED = 'availability_expanded',
  PROVIDER_RESULT_CARD_INDEX_IN_CAROUSEL = 'providerResultCardIndexInCarousel',
  PROVIDER_RESULT_CARD_INDEX_IN_LIST = 'providerResultCardIndexInList',
  TIME_PROVIDER_CARD_VIEWED = 'timeProviderCardViewed',
  ONSITE_LOCATION_DROPDOWN = 'onsite_location_dropdown',
  ONSITE_LOCATION_SHOWN = 'onsite_location_shown',
  ONSITE_LOCATION_CONSIDERED = 'onsite_location_considered',
  ONSITE_LOCATION_SELECTED = 'onsite_location_selected',
  APPOINTMENT_ID = 'appointment_id',
  IS_PROVIDER_SAVED_IN_FAVORITES = 'is_favorited',
  IS_DIRECT_BOOKING = 'is_direct_booking',
  MEMBER_RECOMMENDED_BADGE_SHOWN = 'member_recommended_badge_shown',
  MEMBER_RECOMMENDED_BADGE_ELIGIBLE = 'member_recommended_badge_eligible',
  IPC_FLAG = 'IPC_flag',
  PREFERENCE_OPTIONS_SHOWN = 'preference_options_shown',
  ADJECTIVES_AND_KEYWORDS_SHOWN = 'adjectives_and_keywords_shown',
  TESTIMONIAL_ADJECTIVES = 'testimonial_adjectives',
  BIO_KEYWORDS = 'bio_keywords',
  CHIP_SELECTIONS = 'chip_selections',
  FREE_TEXT_PREFERENCES = 'free_text_preferences',
  FREE_TEXT_PARSING = 'free_text_parsing',
  NEXT_PROVIDER_AVAILABLE = 'next_provider_available',
  PROVIDERS_HIDDEN = 'providers_hidden',
  PROVIDERS_VISIBLE = 'providers_visible',
  MICROSITE_TOPIC = 'microsite_topic',
}

/** Values for the `free_text_parsing` property in member preferences analytics */
export enum FREE_TEXT_PARSING_STATUS {
  FULL = 'Full',
  NONE = 'None',
  ERROR = 'Error',
  TIMEOUT = 'timeout error',
}

export enum S4C_ACTIONS {
  SUBMIT_CONTACT_FORM = 'Submit Contact Form',
}

export enum SURVEY_RESPONSE_PROPERTIES {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export enum ELIGIBILITY_PROPERTIES {
  EAP_ELIGIBILITY_UNCONFIRMED_BANNER = 'eap_eligibility_unconfirmed_banner',
  ELIGIBILITY_ERROR_CAUSE = 'eligibility_error_cause',
  ELIGIBILITY_TYPE = 'eligibility_type',
}

export enum ELIGIBILITY_PROPERTY_VALUE {
  ELIGIBILITY_ERROR_CAUSE_INELIGIBILITY = 'ineligibility',
  ELIGIBILITY_ERROR_CAUSE_UNEXPECTED_ERROR = 'unexpected error',
  ELIGIBILITY_BANNER_ENTRY_POINT = 'eligibility banner',
  REQUIRE_ELIGIBILITY_ENTRY_POINT = 'hard eligibility check',
}

/** `page` prop values */
export enum COMMON_MIXPANEL_PAGE {
  FAVORITE_PROVIDERS = 'favorited providers',
  VIDEO_SESSION = 'video session',
  COACHING_SESSION_MODALITY = 'Coaching - Session Modality',
  MEDS_PROGRAM_OPTIONS = 'med program options',
  PROGRAM_UNAVAILABLE = 'Program Unavailable',
  CONTACT_CARE_TEAM_NAVIGATOR = 'Contact Care Navigator Team',
  CLINICAL_SCREENERS = 'Clinical Screeners',
  LOCATION = 'location',
  PROVIDER_RESULTS = 'Provider Results',
  PROVIDER_RESULTS_CAROUSEL = 'Provider Results Carousel',
  PROVIDER_PROFILE = 'Provider Profile',
  PROVIDER_SCHEDULING_CALENDAR = 'Provider Scheduling Calendar',
  INTERNATIONAL_CONSENT_FOR_DATA_COLLECTION = 'gdpr consent',
  HPI_OVERVIEW = 'hpi overview',
  HPI_CONFIRMATION = 'hpi confirmation',
  ADD_HEALTH_PLAN = 'add health plan',
  ADD_HPI_PAYMENT_METHOD = 'add hpi payment method',
  ADD_INFRACTION_PAYMENT_METHOD = 'add infraction payment method',
  APPOINTMENT_BOOKING_PAYMENT = 'appointment booking payment',
  APPOINTMENT_BOOKING_CONFIRMATION = 'appointment booking confirmation',
  TREATMENT_RECOMMENDATIONS = 'treatment recommendations',
  ASSERTIVE_TRIAGE_TREATMENT_OPTIONS = 'assertive triage treatment options',
  RECOMMENDATIONS_EXPLAINED_PAGE = 'Recommendations Explained Page',
  PREFERENCES_CATEGORY_PAGE = 'Preferences Category Page',
  PREFERENCES_CATEGORY_V2_PAGE = 'Preferences Category V2 Page',
  PREFERENCES_FREE_TEXT_PLAYBACK_PAGE = 'preferences free text playback page',
  PROFILE = 'profile',
  AUD_UNAVAILABLE = 'aud unavailable',
  SEARCH_CARE_REDIRECT = 'high risk direct path diversion',
  ZERO_SEARCH_RESULTS_REDIRECT = 'zero search results redirect',
  CARE_PREFERENCES = 'care preferences',
  APPOINTMENT_SELECTION = 'appointment selection',
  ZERO_RESULTS = 'zero results',
  ZERO_IN_PERSON_RESULTS = 'zero in person results',
  ZERO_TREATMENT_RECOMMENDATIONS = 'zero treatment recommendations',
  GENERAL_CN_CONTACT_PAGE = 'general CN contact page',
  ASSERTIVE_TRIAGE = 'assertive triage',
  CHILD_DIRECT_CN_PATH = 'child direct cn path',
  APPOINTMENT_CONFLICT_MODAL = 'appointment conflict modal',
  TEN_LOCATION_SEARCHES = 'ten location searches',
  PROVIDER_LOADING_SCREEN = 'Provider loading screen',
  APPOINTMENT_BOOKING = 'Appointment booking',
  GLOBAL_NAV_CARE_NAVIGATOR = 'global nav care navigator',
  CN_CONTACT_CONFIRMATION_PAGE = 'CN contact confirmation page',
  CHILD_SELECTION_FORM = 'child selection form',
  APPOINTMENT_REQUEST = 'appointment request',
  INTEGRATED_BENEFITS_PAGE = 'integrated benefits page',
  REGISTER_BY_EMAIL = 'Register by Email input',
  SESSIONS = 'Sessions',
  ASSIGNMENTS_HOME = 'Assignments Home',
  HOME_BASE = 'homebase',
  NONE = 'N/A',
  MICROSITE = 'microsite',
  FIND_EMPLOYER = 'find employer',
  FORKED_TRIAGE = 'forked triage',
  FAMILY_CARE_OPTIONS = 'family care options',
  WELLNESS_TOOLKIT = 'wellness toolkit',
  ELECTION_STRESS_TOOLKIT = 'election stress toolkit',
  ADD_FAMILY_MEMBER = 'Family Hub - Add family member',
  SELF_MANAGED_DEPENDENT_CONFIRMATION = 'Family Hub - Self managed dependent confirmation',
  FAMILY_HUB = 'family hub',
  CARE_TAB = 'care tab',
  PROVIDER_CARE_PATHWAY = 'provider care pathway', // Forked triage
  MARKETING_TOOLKIT = 'marketing toolkit',
  ESSENTIALS = 'essentials',
  LOGIN = 'login',
  ESSENTIALS_HOMEPAGE_WIDGET = 'essentials homepage widget',
  CONTACT_CARE_TEAM_CONFIRMATION = 'Contact Care Navigator Team - Confirmation',
  WELLNESS_CHECK_IN = 'wellness check-in',
  RESOURCE_COORDINATOR = 'resource coordinator page',
  CHILD_INFORMATION = 'create new child page for search',
  CHILD_SELECTION = 'existing child selection page for search',
  PAST_ACTIVITIES = 'past activities list',
  WELCOME_SPLASH = 'logged out home',
  WELCOME_TO_APP = 'welcome to app',
  BIOMETRICS_PROMPT = 'biometrics prompt',
  RESOURCE_COORDINATOR_MICROSITE = 'resource coordinator microsite',
  MESSAGE_PROMPT = 'message prompt',
  DISCLAIMER = 'disclaimer',
  JAILBROKEN_DEVICE_DISABLED = 'jailbroken device disabled',
  ACCOUNT_NOT_FOUND = 'account not found',
  LYRAWEB_MOBILE_DISABLED = 'Lyraweb mobile disabled',
  WEEK_ACTIVITIES = 'week activities',
  ZERO_COACHING_RESULTS = '0 results coaching page',
  MESSAGES = 'Messages',
  LCX_CLIENT_ONBOARDING = 'LCx client onboarding',
  REBOOK = 'rebook',
  SATISFACTION_SURVEY = 'Satisfaction Survey',
  LT_PROGRESS = 'LT Progress',
  UNSUBSCRIBE = 'Unsubscribe',
  ONBOARDING_WELLNESS_GOALS_PAGE = 'onboarding wellness goals page',
  ONE_TIME_PASSCODE = 'OneTimePasscode',
  PERFORMANCE_HUB_MICROSITE = 'performance hub microsite',
  NEURODIVERSITY_TOOLKIT = 'neurodiversity toolkit',
  PARENTAL_LEAVE_COACHING = 'parental leave coaching',
  MANAGER_COACHING = 'manager coaching',
  TOOLKIT_DIRECTORY_PAGE = 'toolkit directory page',
  SIDE_PANEL = 'side panel',
  DEDICATED_CN_MODAL = 'dedicated cn modal',
  ZERO_SEARCH_RESULTS_RECOMMENDED_TREATMENT_OPTION = 'zero search results recommended treatment option',
  HEALTH_CHALLENGES_TOOLKIT = 'health challenges toolkit',
  WOMENS_HEALTH_TOOLKIT = 'womens health toolkit',
  CENTER_OF_EXCELLENCE_TOOLKIT = 'center of excellence toolkit',
  EXHAUSTED_PROVIDER_RECS = 'exhausted provider recs',
  AUTHENTICATOR_MFA = 'mfa app',
  CUSTOM_MICROSITE = 'custom microsite',
  ACTIVITIES_AND_DOCUMENTS = 'activities & documents',
  DBT_HEALTH_PLAN = 'dbt health plan',
  SELF_PAY_ACKNOWLEDGEMENT = 'self_pay_acknowledgement',
  SMART_SEARCH = 'smart search',
  FAQS = 'FAQs',
}

export enum COMMON_SEARCH_FOR_CARE_SCREENS {
  CHILD_SELECTION = 'existing child selection page for search',
  CHILD_INFORMATION = 'create new child page for search',
  CHILD_CONCERN = 'care advocate page for child',
  TRIAGE = 'triage',
  LOCATION_FINDER = 'location input',
  SEARCH_FOR_CARE_LOADING = 'provider loading screen',
  PROVIDER_RESULTS = 'provider recommendations',
  PROVIDER_PROFILE = 'provider profile',
  CALENDAR_BOOKING = 'provider scheduling calendar',
  APPOINTMENT_BOOKING = 'appointment booking page',
  APPOINTMENT_REQUEST = 'appointment request page',
  CHILD_CN_EMAIL_REQUEST = 'child cn email request page',
  TRIAGE_SUMMARY = 'triage summary page',
  HIGH_RISK_MODAL = 'high risk modal',
}

/** `message_location` prop values */
export enum MESSAGE_LOCATION {
  POST_TRIAGE = 'post triage',
  POST_ASSESSMENT = 'post assessment',
  WORDCLOUD = 'word cloud',
  CARE_NAVIGATOR = 'care navigator',
}

/** `button_text` prop values */
export enum MIXPANEL_BUTTON_TEXT {
  CONTACT_A_CARE_NAVIGATOR = 'Contact a Care Navigator',
  GET_IN_TOUCH = 'Get in touch',
}

/** `country_category prop values */
export enum COUNTRY_CATEGORY {
  DOMESTIC = 'Domestic',
  INTERNATIONAL = 'International',
}

export enum LOGIN_EVENTS {
  CLICK_LOGIN = 'click log in', // for login CTA on splash screen that takes user to log in screen
  VIEW_LOGIN_PAGE = 'view login page', // Logged by useTrackScreen for mobile
  LOGIN = 'login', // successful user login (non sso)
  LOGIN_GOOGLE_SSO = 'login with google sso',
  LOGIN_PARTNER_SSO = 'login with partner sso',
  LOGIN_APPLE_SSO = 'login with apple sso',
  REQUEST_PASSWORD_RESET = 'request password reset',
  RESET_PASSWORD_SUCCESS = 'reset password success',
  RESET_PASSWORD_FAILED = 'reset password failed',
  TAPS_BIOMETRICS_PROMPT = 'taps biometrics prompt',
  LOGIN_FAILED = 'login failed',
  CLICK_LOG_OUT = 'click log out',
  CLICK_CONTINUE_WITH_PERSONIFY = 'click continue with personify',
}

export const ELIGIBILITY_EVENTS = {
  VIEW_CONFIRM_ELIGIBILITY_FORM: `view ${COMMON_ELIGIBILITY_SCREENS.CONFIRM_ELIGIBILITY_FORM}`,
  VIEW_CONFIRM_ELIGIBILITY_BANNER: 'view confirm eligibility banner',
}

export enum LCT_TEEN_EMAIL_ERROR_TYPE {
  REGISTERED_EMAIL = 'registered email',
  BELONGS_TO_SELF = 'same as current user',
}

export enum HEALTH_PLAN_UPLOAD_ERROR_TYPE {
  IMAGE_FILE_ERROR = 'image_file_error',
  AUTOFILL_RECOGNITION_ERROR = 'autofill_recognition_error',
}

export enum REGISTRATION_PAGES {
  EMAIL_CAPTURE = 'email capture',
  PARENT_LED_LCT_TEEN_WELCOME_PAGE = 'parent-led lct teen welcome',
  ONE_TIME_PASSCODE_INPUT = 'Registration code input',
}

export enum REGISTRATION_ENTRY_POINTS {
  PARENT_LED_LCT_TEEN_BOOKING = 'parent-led lct teen booking',
  FAMILY_HUB_TEEN_INVITATION = 'family hub teen invitation',
  FAMILY_HUB_ADULT_INVITATION = 'family hub adult invitation',
  AUTO_ENROLLMENT_REGISTRATION = 'auto enrollment',
  WELLNESS_CHECK_IN = COMMON_MIXPANEL_PAGE.WELLNESS_CHECK_IN,
}

export const REGISTRATION_EVENTS = {
  // Create an Account / Find Employer / Email Capture Actions
  CLICK_CREATE_AN_ACCOUNT: 'click create an account',
  SUBMIT_ORG_NAME: 'submit organization name',
  REQUEST_EMAIL_VERIFICATION: 'Request Email Verification',
  CLICK_SIGN_UP_WITH_GOOGLE: 'Click Sign up with Google',
  CLICK_SIGN_UP_WITH_APPLE: 'Click Sign up with Apple',
  CLICK_SIGN_UP_WITH_PERSONIFY: 'click sign up with personify',
  RESEND_EMAIL_VERIFICATION: 'Resend email verification',
  CLICK_OPEN_IN_GMAIL: 'click open in gmail',
  CLICK_OPEN_IN_YAHOO: 'click open in yahoo',
  CLICK_OPEN_IN_OUTLOOK: 'click open in outlook',
  CLICK_OPEN_IN_APPLE_MAIL: 'click open in apple mail',
  INPUT_INCORRECT_COMPANY_NAME: 'input incorrect company name',
  CLICK_UPDATE_EMAIL_ADDRESS: 'click update email address',
  SUBMIT_REGISTRATION_FORM: 'submit registration form',

  // Views
  VIEW_EMAIL_VERIFICATION_PAGE: 'View email verification page',
  VIEW_REGISTRATION_FORM_DEFAULT: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_DEFAULT}`,
  VIEW_REGISTRATION_FORM_PARTNER_SSO: 'View Registration (Partner SSO)',
  VIEW_REGISTRATION_FORM_PARTNER_SSO_HYBRID_LOGIN: 'View Registration (Hybrid Login)',
  VIEW_REGISTRATION_FORM_BACKGROUND_INFO: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_BACKGROUND_INFO}`,
  VIEW_REGISTRATION_FORM_CARE_TEAM: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_CARE_TEAM}`,
  VIEW_REGISTRATION_FORM_HPI_EMAIL: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_HPI_EMAIL}`,
  VIEW_REGISTRATION_FORM_PARENT_LED_TEEN: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_PARENT_LED_TEEN}`,
  VIEW_REGISTRATION_FORM_AUTO_ENROLLMENT: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_AUTO_ENROLLMENT}`,
  VIEW_REGISTRATION_FORM_PROVIDER_LED: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_PROVIDER_LED}`,
  VIEW_FIND_EMPLOYER_PAGE: `view ${COMMON_MIXPANEL_PAGE.FIND_EMPLOYER} page`,
  VIEW_ONE_TIME_PASSCODE_INPUT_PAGE: `view ${REGISTRATION_PAGES.ONE_TIME_PASSCODE_INPUT} page`,

  // Eligibility
  EAP_SOFT_ELIGIBILITY_CHECK_ERROR: 'view eap soft eligibility check error',
  EAP_SOFT_ELIGIBILITY_CHECK_SUCCESS: 'succeed eap soft eligibility check',
  EAP_HARD_ELIGIBILITY_CHECK_ERROR: 'view eap hard eligibility check error',
  EAP_HARD_ELIGIBILITY_CHECK_SUCCESS: 'succeed eap hard eligibility check',

  // 2-instance redirection
  CLICK_REDIRECTION_LINK: 'click redirection link',
  SELECT_REDIRECTION_MODAL_OPTION: 'select redirection modal option',

  REGISTRATION_BACKGROUND_INFO_LEARN_MORE_PRIVACY_POLICY: 'Background info - learn more privacy policy',

  REGISTRATION_SUCCESS: 'Register: Success',
  REGISTRATION_GOOGLE_SSO_SUCCESS: 'Register GoogleSSO: Success',
  REGISTRATION_APPLE_SSO_SUCCESS: 'Register AppleSSO: Success',
  REGISTRATION_PARTNER_SSO_SUCCESS: 'Register PartnerSSO: Success',
  REGISTRATION_PARTNER_SSO_HYBRID_LOGIN_SUCCESS: 'Register Success (Hybrid Login)',
  REGISTRATION_GOOGLE_SSO_FAILURE: 'Register GoogleSSO: Failure',
  REGISTRATION_APPLE_SSO_FAILURE: 'Register AppleSSO: Failure',
  ONBOARDING_SUCCESS: 'onboarding success',
  REGISTRATION_FAILED: 'Registration Failed',
  ONBOARDING_FAILED: 'onboarding failed',
  ONBOARDING_SKIPPED: 'onboarding skipped',

  AUTO_ENROLLMENT_DOB_HARD_CHECK_ERROR: 'auto enrollment dob hard check error',
  AUTO_ENROLLMENT_INVALID_TOKEN: 'auto enrollment invalid token redirect',

  VIEW_REGISTRATION_FORM_DBT_REFERRAL: `View ${COMMON_REGISTRATION_SCREENS.REGISTRATION_FORM_DBT_REFERRAL}`,
  DBT_REFERRAL_INVALID_TOKEN: 'dbt referral invalid token redirect',
  VIEW_DBT_MINOR_ERROR_BANNER: 'view dbt minor error banner',

  HYBRID_LOGIN_BANNER_CLICKED: 'Click Banner Log-in (Hybrid Login)',
}

export enum REGISTRATION_PROPERTIES {
  HAS_VALIDATION_ERROR = 'has_validation_error',
  REGISTRATION_TYPE = 'registration_type',
  PROMOTIONAL_SMS = 'promotional_sms',
  DBT_MINOR_ERROR = 'dbt_minor_error',
}

export enum DBT_MINOR_ERROR_TYPE {
  SELF_MINOR = 'self_minor',
  DEPENDENT_MINOR = 'dependent_minor',
}

export const FAMILY_HUB_EVENTS = {
  CLICK_FAMILY_HUB_EXPLORE_YOUR_OPTIONS: 'click family hub explore your options',
  CLICK_ADD_DEPENDENT: 'click add a dependent',
  CLICK_CONTINUE_ON_ADD_DEPENDENT: 'click continue from add a dependent form',
  CLICK_SEND_INVITE_ON_ADD_DEPENDENT: 'click continue to finish non-child account creation',
  CLICK_GOT_IT_ON_SELF_MANAGED_CONFIRMATION_PAGE: 'click got it to exit add a dependent',
  CLICK_GET_STARTED_DEPENDENT_CONFIRMATION_PAGE: 'click get started after adding child dependent account',
  CLICK_FIND_CARE_LATER_DEPENDENT_CONFIRMATION_PAGE: 'click find care later after adding child dependent account',
  FAMILY_CARE_OPTIONS_PARENT_COACHING: 'family care options parent coaching',
}

export const BCX_EVENTS = {
  JOIN_TEXT_SESSION: 'join live messaging session',
  JOIN_VIDEO_SESSION: 'join video session',
  OPEN_VIDEO_SESSION: 'open video session',
  END_VIDEO_SESSION: 'end video session',
  CLOSE_VIDEO_SESSION: 'close video session',
  GET_VIDEO_SESSION_DATA: 'get video session data',
  SEND_MESSAGE: 'send message',
  SEND_MESSAGE_AGAIN: 'message send failure try again',
  DELETE_MESSAGE: 'message send failure delete',
  VIEW_MESSAGE_CONVERSATION: 'view message conversation',
  START_EXERCISE_ENTRY: 'start exercise entry',
  OPEN_EXERCISE_DRAFT: 'open exercise draft',
  DELETE_EXERCISE_DRAFT: 'delete exercise draft',
  SAVE_EXERCISE_DRAFT: 'save exercise draft',
  SUBMIT_EXERCISE_ENTRY: 'submit exercise entry',
  REVIEW_SUBMITTED_EXERCISE: 'review submitted exercise',
  START_ASSESSMENT: 'start assessment',
  RESUME_ASSESSMENT: 'resume assessment',
  SUBMIT_ASSESSMENT: 'submit assessment',
  REVIEW_SUBMITTED_ASSESSMENT: 'review submitted assessment',
  OPEN_GUIDE_LINK: 'open guide link',
  SUBMIT_LESSON_REVIEW: 'submit lesson review',
  SUBMIT_VIDEO_SESSION_FEEDBACK: 'submit video session feedback form',
  VIDEO_SESSION_ERROR: 'view video session error modal',
  CONFIRM_JOIN_SESSION: 'confirm join session',
  CLICK_END_CALL_BUTTON: 'click end call button',
  YES_END_CALL: 'yes end call',
  DO_NOT_END_CALL: 'do not end call',
  CANCEL_SESSION: 'tap cancel session',
  SUBMIT_PAYMENT: 'Submit Payment',
  CLICK_ADD_REFLECTION: 'click add reflection',
  CLICK_GRAD_PAGE_CTA: 'click grad page CTA',
  SUBMIT_REFLECTION: 'submit new reflection',
  CARE_HOME_PROVIDER_CARD_CLICKED: 'Care Home Provider Card Clicked',
  // TODO: ICE-3255
  VIEW_RESULTS_OVERTIME: 'view results overtime',
  OPEN_MESSAGE_PROVIDER_ALTERNATE_TIMES_MODAL: 'Alternate Times Prompt Button Clicked',
  DISMISS_MESSAGE_PROVIDER_ALTERNATE_TIMES_MODAL: 'Alternate Times Modal Dismiss Button Clicked',
  SEND_PROVIDER_ALTERNATE_TIMES: 'Alternate Times Modal Send Button Clicked',
  SESSIONS_REMINDER_CARD_BUTTON_CLICKED: 'Sessions Reminder Card Button Clicked',
  SESSIONS_REMINDER_CARD_DISMISSED: 'Sessions Reminder Card Dismissed',
  CLICK_AI_COACH_ESSENTIALS_CARD: 'click ai coach essentials card',
  CLICK_AI_COACH_START_OR_CONTINUE_CHAT: 'click ai coach start or continue chat',
  CLICK_AI_COACH_AGREE_AND_CONTINUE: 'click ai coach agree and continue',
  SMS_ENROLLMENT_LOGIN_MODAL_SHOWN: 'SMS Notification modal is surfaced',
  SMS_ENROLLMENT_LOGIN_MODAL_ENROLL_CLICKED: 'Turn On Notification clicked',
  SMS_ENROLLMENT_LOGIN_MODAL_DISMISSED: 'Closing Notification Modal',
  INITIAL_APPOINTMENT_BOOKING_COMPLETE: 'complete initial appointment booking',

  // SCREENS
  VIEW_GUIDE: `view ${COMMON_BCX_SCREENS.GUIDE}`,
  VIEW_ACTIVITIES_LIST: `view ${COMMON_BCX_SCREENS.ACTIVITIES_LIST}`,
  VIEW_LESSON_OVERVIEW: `view ${COMMON_BCX_SCREENS.LESSON_OVERVIEW}`,
  LESSON_VIDEO_PLAYER: `view ${COMMON_BCX_SCREENS.LESSON_VIDEO_PLAYER}`,
  VIEW_VIDEO_PLAYER: `view ${COMMON_BCX_SCREENS.VIDEO_PLAYER}`,
  VIEW_LESSON_REVIEW: `view ${COMMON_BCX_SCREENS.LESSON_REVIEW}`,
  VIEW_EXERCISE_OVERVIEW: `view ${COMMON_BCX_SCREENS.EXERCISE_OVERVIEW}`,
  VIEW_PASSIVE_SI_MESSAGE: `view ${COMMON_BCX_SCREENS.PASSIVE_SI_MESSAGE}`,
  VIEW_ACTIVE_SI_MESSAGE: `view ${COMMON_BCX_SCREENS.ACTIVE_SI_MESSAGE}`,
  VIEW_SUMMARY_PAGE: `view ${COMMON_BCX_SCREENS.SUMMARY_PAGE}`,
  VIEW_ESSENTIALS: `view ${COMMON_BCX_SCREENS.ESSENTIALS}`,
  VIEW_GRAD_PAGE: `view ${COMMON_BCX_SCREENS.GRADUATION_PAGE}`,
  VIEW_COMPLEX_CARE_LANDING_PAGE: `view ${COMMON_BCX_SCREENS.COMPLEX_CARE_LANDING_PAGE}`,
  VIEW_COMPLEX_CARE_SUBPAGE: `view ${COMMON_BCX_SCREENS.COMPLEX_CARE_SUB_PAGE}`,
  VIEW_VIDEO_SESSION_COMPLETED: `view ${COMMON_BCX_SCREENS.VIDEO_SESSION_COMPLETED}`,

  // LINKS
  VIEW_CASELOAD_MANAGEMENT_ARTICLE: 'View Caseload Management Dashboard Zendesk Article',
} as const

export const S4C_EVENTS = {
  CLICK_HOW_IT_WORKS_TREATMENT_RECOMMENDATION_DETAILS: 'click how it works on treatment recommendation details',
  CLICK_FAQ_TREATMENT_RECOMMENDATION_DETAILS: 'click faq on treatment recommendation details',
  CLOSE_TREATMENT_RECOMMENDATION_DETAILS: 'close treatment recommendation details',
  VIEW_TREATMENT_RECOMMENDATION_DETAILS: 'view treatment recommendation details',
  VIEW_TREATMENT_RECOMMENDATIONS: 'view treatment recommendations',
  CLICK_LEARN_MORE_ON_PRIMARY_RECOMMENDATION: 'click learn more on primary recommendation',
  CLICK_OPEN_BCT_FELLOW_VIDEO_MODAL: 'click BCT fellowship video',
  CLICK_OPEN_PRACTICE_INFO_MODAL: 'click practice info provider profile',
  CLICK_PRACTICE_INFO_PHONE_NUMBER: 'call from provider practice info',
  CLICK_PRACTICE_INFO_EMAIL: 'email from provider practice info',
  CLICK_PRACTICE_INFO_GET_DIRECTIONS: 'directions from provider practice info',
  CLICK_PROVIDER_CARD: 'click provider card',
  CLICK_PROVIDER_NAVIGATION_BUTTON: 'click provider navigation button',
  CLICK_FAST_TRACK_BOOKING: 'click fast track booking',
  CLICK_CONTINUE_WITH_BOOKING: 'click continue with booking',
  SHOW_APPOINTMENT_CONFLICT_MODAL: 'show appointment conflict modal',
  VIEW_CARE_PLAN_HOME: `view care plan home`,
  SHOW_HOMEBASE_TOOLKITS: 'show homebase toolkits',
  CLICK_FIND_CARE: 'click find care',
  CLICK_FIND_CARE_CHILD: 'click find care child',
  CREATE_CHILD_ACCOUNT: 'create child account',
  CLICK_CONTINUE_AFTER_SELECTING_CHILD: 'click continue after selecting child',
  CLICK_ADD_ANOTHER_CHILD: 'click add another child',
  VIEW_GDPR_CONSENT: 'view gdpr consent',
  CLICK_AGREE_TO_GDPR_CONSENT: 'agree to gdpr consent',
  CLICK_DECLINE_GDPR_CONSENT: 'click decline gdpr consent',
  VIEW_GDPR_DECLINED_PAGE: 'declined gpr',
  SELECT_PRIMARY_NEED: 'select primary need',
  SELECT_TREATMENT_OPTION: 'select treatment option',
  SELECT_INDIVIDUAL_THERAPY_FROM_AUD: 'select individual therapy from aud',
  CLICK_LEARN_MORE_FOR_MED_PROGRAMS: 'click learn more for med programs',
  REQUEST_BIPOC_PROVIDERS: 'request bipoc providers',
  CLICK_BACK_TO_PROVIDER_RECOMMENDATIONS: 'click back to provider recommendations',
  VIEW_PROVIDER_RECOMMENDATIONS: 'view provider recommendations',
  VIEW_PROVIDER_RECOMMENDATIONS_SINGLE: 'View provider recommendations - single',
  CLICK_PROGRAM_BOOKING_LINK: 'click program booking link',
  VIEW_LOCATION_MATCHER: 'view location input',
  VIEW_ONSITE_LOCATIONS: 'view onsite locations',
  CLICK_APPOINTMENT_TIME: 'click appointment time',
  VIEW_MORE_APPOINTMENT_SLOTS: 'view more appointment slots',
  BOOK_INITIAL_SESSION: 'book initial session',
  CONTACT_CARE_ADVOCATE_FOR_CHILD: 'contact care advocate for child',
  VIEW_PROVIDER_CONTACT_INFO: 'view provider contact info',
  SUBMIT_EMAIL_REQUEST_TO_BOOK: 'submit email request to book',
  VIEW_RECOMMENDATIONS_EXPLAINED: 'view recommendations explained',
  VIEW_MEMBER_PREFERENCES_CATEGORY_PAGE: 'view member preferences category page',
  VIEW_MEMBER_PREFERENCES_LEARN_MORE: 'view learn more about member preferences',
  VIEW_NO_PREFERENCE_MATCHES_MODAL: 'view no preference matches modal',
  VIEW_PREFERENCE: 'view preference',
  VIEW_PREFERENCE_DISCLAIMER: 'view preference disclaimer',
  CLICK_FLEXIBLE_AVAILABILITY: 'click flexible availability',
  CLICK_SPECIFY_AVAILABILITY: 'click specify availability',
  RESET_MEMBER_PREFERENCES: 'reset member preferences',
  CLICK_ADD_MEMBER_PREFERENCES: 'click to add member preferences',
  CLICK_MEMBER_PREFERENCES_MODAL_DONE: 'click done in member preference modal',
  CLICK_MEET_PROVIDERS: 'click meet providers',
  CLICK_TO_CONTACT_CN: 'click to contact cn',
  VIEW_GENERAL_CONTACT_CN_TEAM: 'view general contact cn team page',
  VIEW_GENERAL_CONTACT_CN_TEAM_MODAL: 'view general contact cn team modal',
  CLICK_SCHEDULE_CN_CALL: 'click schedule a cn call',
  CLICK_CHAT_LIVE: 'click chat live',
  SUBMIT_EMAIL_CN: 'submit cn email',
  CONTACT_CN_TEAM_CONFIRMATION: 'contact cn team email confirmation',
  VIEW_PROVIDER_LOADING_SCREEN: 'view provider loading screen',
  VIEW_PROVIDER_SCHEDULING_CALENDAR: 'view provider scheduling calendar',
  VIEW_CHILD_DIRECT_CN_PATH: 'view child direct cn path',
  CLICK_CHILD_EXPLORE_CARE_OPTIONS: 'click child explore care options',
  CLICK_CHILD_PERSONALIZED_GUIDANCE_DROPDOWN: 'click child personalized guidance dropdown',
  VIEW_CHILD_CN_EMAIL_REQUEST_PAGE: 'view child cn email request page',
  CLICK_PROVIDER_VIDEO: 'click provider video',
  CLICK_PROVIDER_VIDEO_FULL_SCREEN: 'click provider video full screen',
  STOP_PROVIDER_VIDEO: 'stop provider video',
  VIEW_PROVIDER_PROFILE: 'view provider profile',
  CLICK_CLINICAL_EXPERIENCE_SHOW_MORE: 'click clinical experience show more',
  PARENT_LED_LCT_TEEN_BOOKING_EMAIL_ERROR: 'parent led lct teen booking email error',
  OPEN_LYRA_CARE_MODAL: 'open lyra care modal',
  OPEN_LYRA_CARE_TESTIMONIALS: 'open lyra care testimonials',
  VIEW_MORE_LYRA_CARE_TESTIMONIALS: 'view more lyra care testimonials',
  CLOSE_LYRA_CARE_MODAL: 'close lyra care modal',
  OPEN_HOW_MATCHING_WORKS_MODAL: 'open how matching works modal',
  CLOSE_HOW_MATCHING_WORKS_MODAL: 'close how matching works modal',
  CLICK_RECOMMENDED_FAMILY_PROGRAM: 'click recommended family program',
  CLOSE_RECOMMENDED_FAMILY_PROGRAM: 'close recommended family program',
  CLICK_CONTINUE_FROM_ADD_A_CHILD_FORM: 'click continue from add a child form',
  CLICK_CONTINUE_TRIAGE_SUMMARY: 'click continue on triage summary page',
  SUBMIT_EMAIL_REQUEST_TO_BOOK_ERROR: 'submit email request to book error',
  CLICK_REQUEST_CALL: 'click request a call from care navigators',
  CLICK_SUBMIT_PHONE: 'click submit phone number',
  OPEN_PROVIDER_SUMMARY_EXPLANATION: 'open provider summary explanation',
  CLOSE_PROVIDER_SUMMARY_EXPLANATION: 'close provider summary explanation',
  CLICK_IN_PAGE_PRODUCT_RATING: 'click in page product rating',
  OPEN_FAQ_MODAL: 'open faq modal',
  CLOSE_FAQ_MODAL: 'close faq modal',
  SCROLL_PROVIDER_RESULTS_CAROUSEL: 'scroll provider results carousel',
  SCROLL_PROVIDER_RESULTS_VERTICAL: 'scroll provider results vertical',
  INITIAL_APPOINTMENT_BOOKING: 'Initial Appointment Booking',
  SUBMIT_CARE_NAVIGATOR_CONSULT_REQUEST: 'request T1 CA consult',
  COLLAPSE_PREFERENCE: 'collapse preference',
  CLICK_DEDICATED_CN_MODAL: 'click dedicated cn modal',
  CLICK_PROVIDER_FIT_FEEDBACK: 'click provider fit feedback',
  CLICK_IN_PAGE_PROVIDER_RATING: 'click in page provider rating',
  SUBMIT_IN_PAGE_PROVIDER_FEEDBACK: 'submit in page provider feedback',
  VIEW_IN_PAGE_PROVIDER_FEEDBACK_CONFIRMATION: 'view in page provider feedback confirmation',
  CLICK_DIRECT_PATH: 'click direct path',
  CLOSE_PROVIDER_FIT_MODAL: 'close provider fit modal',
  CLICK_VIEW_NEXT_PROVIDER: 'click view next provider',
} as const

export const FAVORITED_PROVIDERS_EVENTS = {
  VIEW_FAVORITE_PROVIDERS_PAGE: 'view favorite providers',
  CLICK_FAVORITE_PROVIDER_STAR: 'click on favorite provider star',
  CONFIRM_UNFAVORITE_PROVIDER: 'confirm unfavorite provider',
  VIEW_FAVORITE_PROVIDER_OR_SEARCH_MODAL: 'view favorite provider or search modal',
  VIEW_FIRST_FAVORITE_PROVIDER_MODAL: 'view first favorite provider modal',
}

export const FAVORITED_PROVIDERS_ACTIONS = {
  FAVORITE: 'favorite',
  UNFAVORITE: 'unfavorite',
}

export const FAVORITED_PROVIDERS_PAGE_ENTRY_POINTS = {
  NAV_BAR: 'nav bar',
  HOMEBASE_MODAL: 'homebase modal',
  FIRST_TIME_USER_MODAL: 'first time user modal',
  TOAST: 'toast',
  REENGAGEMENT_EMAIL: 'reengagement email',
  REMINDER_EMAIL: 'reminder email',
  HAMBURGER_MENU: 'hamburger menu',
  PROVIDER_FEEDBACK_MODAL: 'provider feedback modal',
}

export const FAVORITED_PROVIDERS_PAGE_STATES = {
  ALL_AVAILABLE: 'all available',
  SOME_AVAILABLE: 'some available',
  NONE_AVAILABLE: 'none available',
  EXPIRED_OR_NONE_FAVORITED: 'expired or none favorited', // all expired or none favorited
  REFINED_FAVORITES: 'refined favorites', // Some providers cleared via validation
  ALL_PROVIDERS_CLEARED: 'all providers cleared', // All providers cleared via validation
}

export const FAVORITED_PROVIDERS_OTHER_PROPERTIES = {
  PAGE_VERSION: 'page_version',
  PROPERTY: 'property',
}

export const WELLNESS_GOALS_CAROUSEL_EVENTS = {
  SHOW_HOMEBASE_TOOLKITS: 'show homebase toolkits',
  CLICK_SEE_MORE_WELLNESS_TOOLKITS: 'click see more wellness toolkits',
  CLICK_CHANGE_YOUR_GOALS: 'click change your goals',
}

export const ONBOARDING_WELLNESS_GOALS_EVENTS = {
  VIEW_GOALS_WORD_CLOUD: 'view goals word cloud',
  SKIP_GOALS_WORD_CLOUD: 'skip goals word cloud',
  SUBMIT_GOALS_WORD_CLOUD: 'submit goals word cloud',
}

export const S4C_HEADER_EVENTS = {
  CLICK_PATIENT_DROPDOWN_LIST_ON_HOMEBASE: 'click on homebase patient dropdown list',
  SELECT_PATIENT_FROM_PATIENT_DROPDOWN_LIST_ON_HOMEBASE: 'select from homebase patient dropdown list',
  CLICK_PRIMARY_NEED_DROPDOWN_LIST_ON_HOMEBASE: 'click on homebase triage need dropdown list',
  SELECT_PRIMARY_NEED_FROM_PRIMARY_NEED_DROPDOWN_LIST_ON_HOMEBASE: 'select from homebase triage need dropdown list',
  CLICK_EXPLORE_CARE_OPTIONS: 'click explore care options from homebase',
  CLICK_SEARCH_FOR_CARE_MODAL: 'click search for care modal',
  HOVER_FOR_ICON_EXPLANATION: 'hover for icon explanation',
}

export const CARE_TOOL_EVENTS = {
  CLICK_SCHEDULE_VIA_CONNECTED_CALENDAR: 'Click Schedule Via Connected Calendar',
  CLICK_SERVICE_CONFIRMED: 'Click Service Confirmed',
  CLICK_SERVICE_NOT_CONFIRMED: 'Click Service Not Confirmed',
}

export enum PATHWAYS_EVENTS {
  VIEW_PROVIDER_CARE_PATHWAY = 'view provider care pathway',
  VIEW_SELF_CARE_PATHWAY_TILE = 'view self care pathway tile',
  CLICK_EXPLORE_ON_YOUR_OWN = 'click explore on your own',
  VIEW_MEET_WITH_AN_EXPERT_TAB = 'view meet with an expert tab',
  CLICK_MEET_WITH_AN_EXPERT = 'click meet with an expert',
  VIEW_CONTENT_LIBRARY_TAB = 'view content library tab',
  CLICK_ACCESS_FULL_LIBRARY = 'click access full library',
  VIEW_ALL_OFFERINGS_TAB = 'view all offerings tab',
  CLICK_CREATE_ACCOUNT = 'click create account',
  CLICK_CHAT_LIVE = 'click chat live',
  CLICK_FAQS = COMMON_MIXPANEL_EVENTS.CLICK_FAQS,
  CLICK_NOTIFY_ME_ON_LAUNCH = 'click notify me at launch',
  CLICK_SELF_CARE_RESOURCE = 'click self care resource',
  CLICK_MOBILE_APP_DOWNLOAD_LINK = 'click mobile app download link',
}

export enum PATHWAYS_PROPERTIES {
  FAQ_QUESTION = 'FAQ question',
}

export enum TRIAGE_EVENTS {
  VIEW_PRIMARY_NEED = 'view primary need',
  VIEW_WORD_CLOUD_0 = 'view word cloud 0',
  VIEW_WORD_CLOUD_1 = 'view word cloud 1',
  VIEW_SEVERITY_SLIDERS = 'view severity sliders',
  VIEW_IMPAIRMENT_OF_FUNCTIONING = 'view impairment of functioning',
  VIEW_FELT_LIKE_USUAL_SELF_TRIAGE = 'view felt like usual self triage',
  VIEW_CURRENT_THERAPY_TRIAGE = 'view current therapy triage',
  VIEW_PAST_THERAPY_TRIAGE = 'view past therapy triage',
  VIEW_CLINICAL_SCREENERS = 'view clinical screeners',
  VIEW_CARE_EXPERIENCES = 'view care experiences',
  VIEW_LIFE_CHANGES = 'view life changes',
}

export enum COMMON_PREFERENCE_CATEGORIES {
  MEETING_STYLE = 'meeting style',
  CARE_LANGUAGE = 'care language',
}

// Generally used for recording where the Contact CN link click originates from
export enum MEMBER_PREFERENCE_ENTRY_POINTS {
  NO_PREFERENCES_MODAL = 'no preferences modal',
  LEARN_MORE_MODAL = 'preferences learn more modal',
  CARE_LANGUAGE = 'care language',
}

export enum CHILD_ACCOUNT_ENTRY_POINTS {
  CREATE_CHILD_ACCOUNT_PROFILE = 'member profile - add a minor',
}

export enum GRADUATION_PAGE_CTA {
  ESSENTIALS_GRADUATION_CONTENT = 'essential graduation content',
  FINAL_ASSESSMENT = 'final assessment',
  POST_CARE_ASSESSMENT = 'post-care assessment',
  PAST_ACTIVITIES = 'past activities',
  ESSENTIALS_HOMEPAGE = 'essentials homepage',
}

export enum ESSENTIALS_ENTRY_POINTS {
  GRAD_PAGE_HOMEPAGE_CTA = 'Grad Page - Homepage CTA',
  GRAD_PAGE_GRAD_TIPS_CTA = 'Grad Page - Grad Tips CTA',
  TRAUMA_CHECKIN = 'trauma-checkin',
}

export enum PATHWAYS_ENTRY_POINTS {
  MICROSITE = 'microsite',
  SELF_CARE = 'selfcare pathway',
  LOGIN_PAGE = 'login page',
  ALL_OFFERINGS = 'all offerings',
  FAQS = 'FAQs',
  CARE_NAVIGATOR = 'care navigator',
  INTEGRATED_BENEFITS = 'integrated benefits',
  GLOBAL_NAV_BAR = 'global nav bar',
  FORKED_TRIAGE_FOOTER = 'footer',
  HOMEBASE = 'homebase',
  SEARCH_FOR_CARE_MODAL = 'search for care modal',
}

export enum COMPLEX_CARE_ENTRY_POINTS {
  LANDING_PAGE = 'complex care landing page',
}

export enum FORKED_TRIAGE_ENTRY_POINTS {
  FIND_CARE_FOR_SELF = 'find care for yourself',
  FIND_CARE_FOR_CHILD = 'find care for child',
  COACHING_FOR_SELF = 'coaching for yourself',
  COACHING_FOR_CHILD = 'coaching for child',
  MEDICATION_CONSULTATION_FOR_SELF = 'medication consultation for yourself',
  FIND_CARE_FOR_FAMILY = 'find care for family',
  FIND_CARE_FOR_COUPLES = 'find care for couples',
}

export enum PATHWAYS_PAGE {
  COMING_SOON = 'coming soon',
  CUSTOMER_MICROSITE = 'customer microsite',
  AUTO_ENROLLMENT_PATHWAYS = 'auto-enrollment pathways',
}

export enum TRIAGE_ENTRY_POINTS {
  HOMEBASE = 'homebase',
  FORKED_TRIAGE = 'forked triage',
  ESSENTIALS = 'essentials',
  FAMILY_CARE_OPTIONS = 'family care options',
  WELLNESS_TOOLKIT = 'wellness toolkit',
  CARE_TAB = 'care tab',
  PROFILE_ELIGIBILITY = 'profile eligibility',
  WELLNESS_CHECK_IN = COMMON_MIXPANEL_PAGE.WELLNESS_CHECK_IN,
  TRAUMA_CHECKIN = 'trauma checkin',
  SEARCH_FOR_CARE_MODAL = 'search for care modal',
  SEARCH_FOR_CARE_MODAL_FORKED_TRIAGE = 'search for care modal forked triage',
  INTEGRATED_BENEFITS_PAGE = COMMON_MIXPANEL_PAGE.INTEGRATED_BENEFITS_PAGE,
  FAVORITED_PROVIDERS = 'favorited providers',
  PUSH_NOTIFICATION = 'push notification',
  TOOLKIT_DIRECTORY = 'toolkit directory',
  NEURODIVERSITY = 'neurodiversity toolkit',
  HEALTH_CHALLENGES_TOOLKIT = 'health challenges toolkit',
  WOMENS_HEALTH_TOOLKIT = 'womens health toolkit',
  CENTER_OF_EXCELLENCE_TOOLKIT = 'center of excellence toolkit',
  REENGAGEMENT_EMAIL_REPLAY_SEARCH = 'reengagement email replay search',
  HOMEBASE_CARE_CAROUSEL = 'homebase care carousel',
  SMART_SEARCH_RESULTS = COMMON_ENTRY_POINTS.SMART_SEARCH_RESULTS,
}

export enum PATHWAYS_OPTIONS {
  EXPERT = 'meet with an expert',
  CONTENT_LIBRARY = 'content library',
  FAMILY_SUPPORT = 'family support',
}

export const MIXPANEL_EVENT_ATTRIBUTES = {
  ENTRY_POINT: {
    NAV_BAR: 'Nav Bar',
    LIBRARY: 'Library',
    HOME_BASE: 'Home Base',
    DIRECT_LINK: 'Direct Link',
    WELLNESS_TOOLKIT: 'Wellness Toolkit',
    INTEGRATED_BENEFITS: 'Integrated Benefits',
    INTEGRATED_BENEFITS_MODAL: 'Integrated Benefits Modal',
    SMART_SEARCH_RESULTS: COMMON_ENTRY_POINTS.SMART_SEARCH_RESULTS,
  },
}

export enum HEALTH_PLAN_EVENTS {
  CLICK_LEARN_MORE_ABOUT_HPI = 'click learn more about hpi',
  ADD_HEALTH_PLAN = 'add health plan',
  ADD_SECONDARY_HEALTH_PLAN = 'add secondary health plan',
  ADD_HPI_PAYMENT_METHOD = 'add hpi payment method',
  ADD_INFRACTION_PAYMENT_METHOD = 'add infraction payment method',
  VIEW_ADD_HEALTH_PLAN = 'view add health plan',
  VIEW_HPI_PAYMENT_METHOD = 'view hpi payment method',
  VIEW_INFRACTION_PAYMENT_METHOD = 'view infraction payment method',
  VIEW_APPOINTMENT_DETAILS = 'view appointment details',
  REMOVE_HEALTH_PLAN = 'remove health plan',
  REMOVE_HPI_PAYMENT_METHOD = 'remove hpi payment method',
  REMOVE_INFRACTION_PAYMENT_METHOD = 'remove infraction payment method',
  CLICK_UPLOAD_FRONT_OF_HEALTH_PLAN_CARD = 'click upload front of health plan card',
  CLICK_UPLOAD_BACK_OF_HEALTH_PLAN_CARD = 'click upload back of health plan card',
  VIEW_HEALTH_PLAN_CARD_SUCCESSFUL_UPLOAD = 'view health plan card successful upload',
  VIEW_HEALTH_PLAN_CARD_UPLOAD_ERROR = 'view health plan card upload error',
  VIEW_HEALTH_PLAN_RECONCILIATION_WARNING = 'view health plan reconciliation warning',
  VIEW_HEALTH_PLAN_CHC_FAILURE = 'view health plan chc failure',
  CLICK_CONTACT_US_HP_ELIGIBILITY_ERROR = 'click contact us - hp eligibility error',
  CLICK_ENROLL_INTO_SELF_PAY = 'click enroll into self-pay',
  VIEW_SELF_PAY_ACKNOWLEDGEMENT = 'view self-pay acknowledgement',
  CLICK_SELF_PAY_ACKNOWLEDGEMENT_ENROLLED = 'click self-pay acknowledgement enrolled',
  VIEW_NEXT_UP_DBT_CARE_INTAKE = 'view next up dbt care intake',
  CLICK_DBT_CARE_INTAKE = 'click dbt care intake',
}

export enum DBT_HEALTH_PLAN_ERROR_BANNER {
  HP_RECONCILIATION_WARNING = 'hp reconciliation warning',
  HP_NOT_VERIFIED = 'hp not verified',
  CHC_API_FAILURE = 'chc api failure',
  UNQUALIFIED_HP = 'unqualified hp',
}

export enum DBT_HEALTH_PLAN_ENTRY_POINT {
  HOMEBASE = 'homebase',
  ONBOARDING_REGISTRATION = 'onboarding/registration',
  ACCOUNT = 'account',
  INFO_NEEDED_MODAL = 'info needed modal',
  HPI_ONLY_BANNER = 'hpi-only banner',
}

export enum DBT_HEALTH_PLAN_PROPERTIES {
  DBT_HP_ERROR_BANNER = 'dbt_hp_error_banner',
  HEALTH_PLAN_NAME_OPTIONS = 'health_plan_name_options',
  SUBMIT_BUTTON_TEXT = 'submit_button_text',
}

export enum DBT_TEEN_EVENTS {
  VIEW_DEPENDENT_CONTACT_INFO = 'view dependent contact info',
  ADD_DEPENDENT_CONTACT_INFO = 'add dependent contact info',
}

export enum DBT_TEEN_PROPERTIES {
  IS_HPHS = 'is_hphs',
  IS_TEEN_DEPENDENT = 'isTeenDependent',
}

export enum DBT_TEEN_ENTRY_POINT {
  TRIAGE = 'triage',
  MEMBER_PROFILE = 'member_profile',
  PROVIDER_REFERRAL = 'provider_referral',
}

export enum FORKED_TRIAGE_PROGRAM_DETAIL {
  DBT_TEENS = 'dbt_teens',
}

export enum SUBMIT_BUTTON_VALUES {
  ADD = 'add',
  RECHECK = 'recheck',
}

export enum SELF_PAY_EVENTS {
  VIEW_TRANSITION_SELF_PAY_PAYMENT_METHOD = 'view transition self-pay payment method',
  ADD_TRANSITION_SELF_PAY_PAYMENT_METHOD = 'add transition self-pay payment method',
  VIEW_SELF_PAY_APPOINTMENT_DETAILS = 'view self-pay appointment details',
  CLICK_DOWNLOAD_SELF_PAY_INVOICE = 'click download self-pay invoice',
  ADD_ACCOUNT_PAYMENT_METHOD = 'add account payment method',
  UPDATE_ACCOUNT_PAYMENT_METHOD = 'update account payment method',
  REMOVE_ACCOUNT_PAYMENT_METHOD = 'remove account payment method',
  CLICK_SELF_PAY_TRANSITION_HOMEBASE_BANNER = 'click self-pay transition homebase banner',
}

export enum EAP_TO_HPI_BANNER_EVENTS {
  CLICK_EAP_TO_HPI_TRANSITION_HOMEBASE_BANNER = 'click EAP to HPI transition homebase banner',
  DISMISS_EAP_TO_HPI_TRANSITION_HOMEBASE_BANNER = 'dismiss EAP to HPI transition homebase banner',
  CLICK_EAP_TO_HPI_TRANSITION_ACCOUNT_BANNER = 'click EAP to HPI transition account banner',
  DISMISS_EAP_TO_HPI_TRANSITION_ACCOUNT_BANNER = 'dismiss EAP to HPI transition account banner',
}

export enum EAP_TO_HPI_BANNER_PROPERTIES {
  EAP_SESSIONS_LEFT = 'eap_sessions_left',
  BANNER_TYPE = 'banner_type',
}

export enum SELF_PAY_PAYMENT_TYPE {
  SELF_PAY = 'self-pay',
  NO_SHOWS_CANCELLATIONS = 'no-shows/cancellations',
  HEALTH_PLAN_COST_SHARING = 'health plan cost sharing',
}

export enum SELF_PAY_PROPERTIES {
  PAYMENT_TYPE = 'payment_type',
}

export const MIXPANEL_CARE_NAVIGATOR_BUTTON_TEXT = (isInternationalUser: boolean) => {
  if (isInternationalUser) {
    return MIXPANEL_BUTTON_TEXT.CONTACT_A_CARE_NAVIGATOR
  } else {
    return MIXPANEL_BUTTON_TEXT.GET_IN_TOUCH
  }
}

export enum MIXPANEL_CARE_NAVIGATOR_OPTIONS_LIST {
  SIDE_PANEL_DOMESTIC = 'Call | Live Chat | Email | Schedule a call',
  DOMESTIC = 'Call | Schedule a Call | Email',
  INTERNATIONAL = 'Call | Live Chat | Email',
  ZERO_IN_PERSON_MODAL = 'Call | Schedule a Call',
}

export const MIXPANEL_CARE_NAVIGATOR_OPTIONS = ({
  isInternationalUser,
  isSidePanel,
}: {
  isInternationalUser?: boolean
  isSidePanel?: boolean
}) => {
  if (isInternationalUser) {
    return MIXPANEL_CARE_NAVIGATOR_OPTIONS_LIST.INTERNATIONAL
  } else if (isSidePanel) {
    return MIXPANEL_CARE_NAVIGATOR_OPTIONS_LIST.SIDE_PANEL_DOMESTIC
  } else {
    return MIXPANEL_CARE_NAVIGATOR_OPTIONS_LIST.DOMESTIC
  }
}

export enum LATEST_PROVIDER_EVENTS {
  VIEW_LATEST_PROVIDER_RECOMMENDATIONS = 'view last provider recommendations',
  CLICK_START_NEW_SEARCH = 'click start new search',
  REENGAGEMENT_EMAIL_TIME_ELAPSED_MODAL = 'reengagement email time elapsed modal',
  REENGAGEMENT_EMAIL_REPLAY_SEARCH = 'reengagement email replay search',
  REENGAGEMENT_EMAIL_ZERO_AVAILABILITY_MODAL = 'reengagement email zero availability modal',
  REENGAGEMENT_EMAIL_EXIT_PATH_MODAL = 'reengagement email exit path modal',
  CLICK_LAST_PROVIDER_RECOMMENDATION = 'click last provider recommendation', // from the favorited providers page currently
}

export enum LANGUAGE_PROPERTIES {
  ACTIVE_LANGUAGE = 'active_language',
  DEVICE_PREFERRED_LANGUAGES = 'device_preferred_languages',
}

export enum LATEST_PROVIDER_PROPERTIES {
  MATCHER_REQUEST_ID = 'matcher_request_id', // request_id of the last search
  LAST_SEARCH_TIME = 'last_search_time',
  PROVIDER_AVAILABILITY = 'provider_availability',
  PAST_PROVIDER_RESULTS_FLAG = 'past_provider_results_flag',
  PAST_SEARCH_AVAILABLE = 'past_search_available',
  ENTRYPOINT = 'entryPoint',
}

export enum PROVIDER_VIDEO_PROPERTIES {
  PROVIDER_VIDEO_FLAG = 'provider_video_flag',
  PLAYED_INTERVALS = 'playedIntervals',
  VIDEO_ENDED = 'videoEnded',
  VIDEO_DURATION = 'videoDuration',
  PERCENT_VIEWED = 'percentViewed',
  VIDEO_ID = 'videoId',
}

export enum INTEGRATED_BENEFITS_EVENTS {
  VIEW_INTEGRATED_BENEFITS_EXPLORER = 'view integrated benefits explorer',
  VIEW_INTEGRATED_BENEFITS_PAGE = 'view integrated benefits page',
  CLICK_INTEGRATED_BENEFIT = 'click integrated benefit',
  VIEW_INTEGRATED_BENEFITS_ENTRYCARD = 'view integrated benefits entrycard',
  DISMISS_INTEGRATED_BENEFITS_ENTRYCARD = 'dismiss integrated benefits entrycard',
  CLICK_SCHEDULE_RESOURCE_COORDINATOR_CALL = 'click schedule a resource coordinator call',
  CLICK_EXPLORE_ALL_YOUR_BENEFITS_FROM_HOMEBASE = 'click explore all your benefits from homebase',
  CLICK_SHOW_ALL_BENEFITS_LINK = 'click show all benefits link',
  CLICK_BENEFIT_SHORTCUT_PILL = 'click benefit shortcut pill',
  VIEW_INTEGRATED_BENEFIT_MODAL = 'view integrated benefit modal',
  CLICK_INTEGRATED_BENEFIT_SSO = 'click integrated benefit sso',
  CLOSE_INTEGRATED_BENEFIT_MODAL = 'close integrated benefit modal',
  VIEW_INTEGRATED_BENEFIT_TILES_COVERAGE_INFO = 'view integrated benefit tiles coverage info',
  CLICK_INTEGRATED_BENEFIT_EXPLORER_TAB = 'click integrated benefits explorer tab',
}

export enum INTEGRATED_BENEFITS_HOMEBASE_ENTRYPOINT {
  BENEFITS_TILE_ADDITIONAL = 'homebase - benefits tile - additional',
  BENEFITS_TILE_EXPLORE = 'homebase - benefits tile - explore',
  SHOW_ALL_LINK = 'show all link',
}

export enum INTEGRATED_BENEFITS_SECTION_TYPE {
  FEATURED = 'Featured',
  CORE_BENEFITS = 'Core benefits',
  WORKPLACE_BENEFITS = 'Workplace benefits',
  SPECIALIZED_CARE = 'Specialized care',
  MORE_BENEFITS_FROM_COMPANY = 'More benefits from company',
  CURATED_FOR_YOU = 'Curated for you',
}

export enum INTEGRATED_BENEFITS_SECTION_PROPERTIES {
  BENEFITS_DIRECTORY_SECTION = 'benefits_directory_section',
  PILL_LABEL = 'pill_label',
  CLICKED_TAB = 'clicked_tab',
}

export enum INTEGRATED_BENEFITS_CATEGORY {
  LYRA_OFFERING = 'lyraOffering',
}

export enum INTEGRATED_BENEFITS_VENDOR {
  ONE_ON_ONE_SESSIONS = '1 on 1 sessions',
  CARE_FOR_A_CHILD = 'care for a child',
  WELLNESS_CHECK_IN = 'wellness check-in',
  LIVE_EVENTS = 'live events',
  ONSITE_THERAPY = 'onsite therapy',
  MEDICATION_CONSULTATION = 'medication consultation',
  TREATMENT_FOR_ALCOHOL_OR_SUBSTANCE_USE = 'treatment for alcohol or substance use',
  COMPLEX_CARE = 'complex care',
  SUPPORT_FOR_SELF_HARM = 'support for self-harm',
  INTENSIVE_OUTPATIENT_PROGRAM = 'intensive outpatient program',
  PARTIAL_HOSPITALIZATION_PROGRAM = 'partial hospitalization program',
  RESIDENTIAL_TREATMENT = 'residential treatment',
  INPATIENT_HOSPITALIZATION = 'inpatient hospitalization',
  NICOTINE_QUITLINE = 'nicotine quitline',
  NEURODIVERSITY_TOOLKIT = 'neurodiversity toolkit',
  CRITICAL_INCIDENT_SUPPORT_TOOLKIT = 'critical incident support toolkit',
  WOMENS_HEALTH_TOOLKIT = 'womens health toolkit',
  HEALTH_CHALLENGES_TOOLKIT = 'health challenges toolkit',
  DISASTER_RESPONSE = 'disaster response',
}

export enum QUICK_LINKS_EVENTS {
  CLICK_ESSENTIALS_POPULAR_CATEGORY = 'click essentials popular category',
  SHOW_POPULAR_ESSENTIALS_CATEGORIES = 'show popular essentials categories',
}

export enum TRIAGE_SUMMARY_PROPERTIES {
  TOOLKIT_TOPIC = 'toolkit_topic',
  ENTRY_POINT = 'entry_point',
}

export enum QUICK_LINKS_EVENTS_PROPERTIES {
  DESTINATION_URL = 'destination_url',
  CATEGORY_NAME = 'category_name',
  CATEGORIES_SHOWN = 'categories_shown',
}

export enum WELLNESS_TOOLKIT_EVENTS {
  CLICK_HOMEBASE_PROMOTION = 'click homebase bcc promotion',
  VIEW_WELLNESS_TOOLKIT = 'view wellness toolkit',
  CLICK_TO_ESSENTIALS_RESOURCE = 'click wellness toolkit resource',
  CLICK_TO_PROVIDER_SEARCH = 'click direct path search',
  CLICK_TO_SOMETHING_ELSE = 'click wellness toolkit something else',
  CLICK_TO_PROGRAM_AWARE_TRIAGE_SEARCH = 'click program aware triage search',
  CLICK_EXPLORE_MORE = 'click explore more toolkit resources',
}

export enum TOOLKIT_EVENTS {
  CLICK_TOOLKIT_TOPIC = 'click toolkit topic',
  CLICK_SHOW_ALL_TOPICS = 'click show all toolkit topics',
  CLICK_TOOLKIT_SOMETHING_ELSE = 'click toolkit something else',
  CLICK_NAVIGATE_HOMEBASE_TOOLKITS_CAROUSEL = 'click navigate homebase toolkits carousel',
}

export enum TOOLKIT_PROPERTIES {
  RECOMMENDED_TOOLKIT_TOPICS_SHOWN = 'recommended_toolkit_topics_shown',
  MORE_RESOURCES_TOOLKIT_TOPICS_SHOWN = 'more_resources_toolkit_topics_shown',
}

export enum TOOLKIT_BENEFIT_NAME {
  TRAUMA_TOOLKIT = 'trauma toolkit',
  NEURODIVERSITY_TOOLKIT = 'neurodiversity center of excellence toolkit',
}

export enum WELLNESS_TOOLKIT_EVENTS_PROPERTIES {
  TOOLKIT_TOPIC = 'toolkit_topic',
  IS_PARENT_COACHING_SHOWN = 'is_parent_coaching_shown',
  LAST_SEARCH_TIME = 'last_search_time',
  CUSTOMER_CONFIGURATION = 'customer_configuration',
  USER_TIMEZONE = 'user_timezone',
  PAGE = 'page',
  DESTINATION_URL = 'destination_url',
}

export const DISASTER_RESPONSE = 'disaster response'

export const CONTACT_CN_ENTRY_POINTS = {
  DISASTER_RESPONSE,
} as const

export const TRAUMA_CHECK_IN_ENTRY_POINTS = {
  DISASTER_RESPONSE,
} as const

export enum ESSENTIALS_CONTENT_EVENTS_PROPERTIES {
  ARTICLE_TITLE = 'article_title',
  DESTINATION_SLUG = 'destination_slug',
  CONTENT_FORMAT = 'content_format',
  CONTENT_TYPE = 'content_type',
  ARTICLE_NAME = 'article_name',
}

export enum ESSENTIALS_ARTICLE_EVENTS_PROPERTIES {
  TITLE = 'title',
  CONTENT_ID = 'content_id',
  CONTENT_SUBTOOLS = 'content_subtools',
  CONTENT_SUBTOPICS = 'content_subtopics',
  CONTENT_TOOLS = 'content_tools',
  CONTENT_TOPICS = 'content_topics',
}

export enum ESSENTIALS_LIVE_EVENTS_PROPERTIES {
  EVENTS_IN_CAROUSEL = 'events_in_carousel',
  EVENT_ID = 'event_id',
  EVENT_TIMES = 'event_times',
  EVENT_FACILITATORS = 'event_facilitators',
  EVENTS_SHOWN_RANK = 'events_shown_rank',
  DESTINATION_URL = 'destination_url',
  HOMEBASE_EVENT_RANK = 'homebase_event_rank',
  FROM_HOMEBASE_CARD = 'from_homebase_card',
}

export enum ESSENTIALS_CAROUSEL_EVENTS {
  CLICK_ESSENTIALS_CONTENT = 'click essentials content',
  CLICK_ESSENTIALS_DISCOVER_MORE = 'click essentials discover more',
  CLICK_ESSENTIALS_CAROUSEL_ITEM = 'click essentials carousel item',
}

export enum SMART_SEARCH_EVENTS {
  CLICK_SMART_SEARCH = 'click smart search',
  VIEW_SMART_SEARCH_PAGE = 'view smart search page',
  ENTER_SMART_SEARCH_TEXT = 'enter smart search text',
  VIEW_SMART_SEARCH_RESULTS_PAGE = 'view smart search results page',
  CLICK_SMART_SEARCH_PILL = 'click smart search pill',
  /** A result (row/card/tile/care option) was clicked — the click-time counterpart to VIEW_SMART_SEARCH_RESULTS_PAGE */
  CLICK_SMART_SEARCH_RESULT = 'click smart search result',
}

export enum SMART_SEARCH_PROPERTIES {
  /** UUID minted on search entry, shared by every event within a single smart search session */
  SMART_SEARCH_SESSION_ID = 'smart_search_session_id',
  SMART_SEARCH_QUERY_ID = 'smart_search_query_id',
  /** Nested data describing the result sections and items shown */
  SMART_SEARCH_RESULTS = 'smart_search_results',
  /** Number of result sections (categories) shown */
  SMART_SEARCH_RESULT_SECTIONS_SHOWN_NUM = 'smart_search_result_sections_shown_num',
  /** Total number of result items shown across all sections */
  SMART_SEARCH_RESULT_ITEMS_SHOWN_NUM = 'smart_search_result_items_shown_num',
  QUICK_LINK_ID = 'quick_link_id',
  /**
   * The clicked result as "<section_id>: <item_id>" — its section (Level 1) and item (Level 2b),
   * e.g. "care_options: child_triage". Uses the same vocabulary as SMART_SEARCH_RESULTS.
   */
  SMART_SEARCH_RESULT_CLICKED = 'smart_search_result_clicked',
  /**
   * The clicked result's rank as "<section_rank>: <item_rank>" (both 1-based) — the section's
   * position among shown sections and the item's position within that section, e.g. "1: 2".
   */
  SMART_SEARCH_RESULT_CLICKED_RANK = 'smart_search_result_clicked_rank',
}

export enum ESSENTIALS_LIVE_EVENTS_EVENTS {
  SHOW_EXPERT_LED_EVENTS = 'show expert led events',
  CLICK_EXPERT_LED_EVENT = 'click expert led event',
  CLICK_SHOW_ALL_EXPERT_LED_EVENTS = 'click show all expert led events',
  EVENTS_DETAIL_VIEW = 'Event Details View',
  EVENT_REGISTRAION = 'Event Registration',
}

export enum FORKED_TRIAGE_EVENTS {
  VIEW_PROGRAM_TYPE = 'view program type',
  CLICK_PROGRAM_TYPE = 'click program type',
  CLICK_FIND_CARE_FOR_YOURSELF = 'click find care for yourself',
  CLICK_FIND_CARE_FOR_CHILD = 'click find care for a child or teen',
  CLICK_COACHING_FOR_YOURSELF = 'click coaching for yourself',
  CLICK_COACHING_FOR_PARENT = 'click coaching for parent',
  CLOSE_COACHING_FOR_PARENT = 'close coaching for parent',
  CLICK_MEDICATION_CONSULTATION_FOR_YOURSELF = 'click medication consultation for yourself',
  CLOSE_MEDICATION_CONSULTATION_FOR_YOURSELF = 'close medication consultation',
  CLICK_TO_SOMETHING_ELSE = 'click wellness toolkit something else',
  CLICK_COUPLES_THERAPY = 'click couples therapy',
  CLICK_FAMILY_THERAPY = 'click family therapy',
  CLOSE_FAMILY_THERAPY = 'close family therapy',
  CLICK_DIRECT_PATH = 'click direct path',
  CLICK_ONSITE = 'click onsite',
  FORKED_TRIAGE_AVAILABLE_PROGRAMS = 'forked triage available programs',
}

export enum FORKED_TRIAGE_PROPERTIES {
  AVAILABLE_PROGRAMS = 'available_programs',
  PROGRAM_DETAIL = 'program_detail',
}

export enum SELF_CARE_EVENTS {
  CLICK_SELF_CARE_CONTINUE = 'click self care topic preference - continue',
  CLICK_SELF_CARE_SKIP = 'click self care topic preference - skip',
}

export enum SELF_CARE_EVENTS_PROPERTIES {
  SELECTED_TOPIC_PREFERENCE = 'selected topic preference',
}

export enum INACTIVITY_EVENTS {
  RESUME = 'dismiss session timeout modal',
  SIGN_OUT = 'log out via session timeout modal',
  INACTIVE = 'display timeout modal to inactive user',
}

export enum INACTIVITY_PROPERTIES {
  USER_SIGN_OUT = 'user clicked sign out',
  SESSION_EXPIRED = 'session timed out',
}

export enum PROFILE_PAGES {
  SETTINGS = 'member profile - settings',
  SESSION_HISTORY = 'member profile - session history',
  PRIVACY_AND_LEGAL = 'member profile - privacy and legal',
  HEALTH_PLAN_AND_PAYMENT_INFO = 'member profile - health plan and payment information',
  ELIGIBILITY = 'member profile - eligibility',
  ACCOUNT_HOME = 'member profile - home',
  CHANGE_EMAIL = 'member profile - change email',
  CHANGE_EMAIL_ONE_TIME_PASSCODE = 'member profile - change email one time passcode',
  DISPLAY_LANGUAGE = 'member profile - display language',
  LOCATION_AND_TIMEZONE = 'member profile - location and timezone',
  LYRA_UPDATES = 'member profile - lyra updates',
  MFA = 'member profile - multi-factor authentication',
  CUSTOM_NOTIFICATIONS = 'member profile - custom notifications',
  NOTIFICATIONS = 'member profile - notifications',
  PHONE_NUMBER = 'member profile - phone number',
  CHILD_INFO = 'child profile - personal info',
  PERSONAL_INFO = 'member profile - personal info',
  UPDATE_INFO_QUESTIONNAIRE = 'member profile - user info questionnaire',
  VERIFY_EMAIL_UPDATE_USER_INFO = 'member profile - verify email update user info',
  CHANGE_THEME = 'member profile - change theme',
  ADD_FAMILY_MEMBER = 'member profile - add family member',
  FORGOT_PASSWORD = 'member profile - forgot password',
}

export enum TRANSFER_ACCOUNT_PAGES {
  FIND_EMPLOYER = 'employer account transfer search',
  TRANSFER_ACCOUNT = 'employer account transfer',
  TRANSFER_ACCOUNT_CONFIRMATION = 'employer account transfer confirmation',
  CHOOSE_MULTI_INSTANCE_SUBPOPULATION = 'employer account transfer - choose multi-instance subpopulation',
  CHOOSE_ONE_MICROSITE_SUBPOPULATION = 'employer account transfer - choose one microsite subpopulation',
}

export enum TRANSFER_ACCOUNT_EVENTS {
  CLICK_PREVIOUS_EMPLOYER_ACCOUNT = 'click previous employer account',
  CLICK_TRANSFER_ACCOUNT_CONFIRMATION = 'click transfer account',
  CLICK_SUBMIT_TRANSFER_ACCOUNT_FORM = 'click submit employer account transfer',
  VIEW_HARD_ELIGIBILITY_ERROR = 'view employer account transfer hard eligibility error',
  SUCCESSFULLY_TRANSFERRED_ACCOUNT = 'successfully transferred account',
  CLICK_FIND_EMPLOYER = 'click find employer',
  CLICK_SEARCH_FOR_CARE_IN_NEW_EMPLOYER_COST_MESSAGE = 'click search for care in new employer cost message',
  CLICK_TRANSFER_ACCOUNT_ELIGIBILITY_PAGE = 'click transfer your Lyra account',
  CLICK_TRANSFER_ACCOUNT_FIND_EMPLOYER = 'click employer account transfer search',
  CLICK_TRANSFER_ACCOUNT_CHOOSE_MULTI_INSTANCE_SUBPOPULATION = 'click employer account transfer - choose multi-instance subpopulation',
  CLICK_TRANSFER_ACCOUNT_CHOOSE_ONE_MICROSITE_SUBPOPULATION = 'click employer account transfer - choose one microsite subpopulation',
  VIEW_EMPLOYER_NOT_FOUND_ERROR = 'view employer not found during account transfer',
}

export enum TRANSFER_ACCOUNT_PROPERTIES {
  USER_ENTERED_EMPLOYER_NAME = 'user_entered_employer_name',
  SUCCESS_BANNER = 'employer account transfer success banner',
}

export enum WELLNESS_CHECK_IN_PAGES {
  OVERVIEW = 'wellness check-in overview',
  HOW_ARE_YOU_FEELING_TODAY = 'wellness check-in question - how are you feeling today',
  MOOD_AND_EMOTIONS = 'wellness check-in question - mood and emotions',
  RESILIENCE_AND_COPING = 'wellness check-in question - resilience and coping',
  ROLES_AND_RELATIONSHIPS = 'wellness check-in question - roles and relationships',
  PHYSICAL_HEALTH_AND_ACTIVITY = 'wellness check-in question - physical health and activity',
  FINANCES_AND_WORK = 'wellness check-in question - finances and work',
  SPIRITUALITY_AND_EXPLORATION = 'wellness check-in question - spirituality and exploration',
  SOCIAL_CARE = 'wellness check-in question - social care',
  PREVIEW = 'wellness check-in preview results',
  RESULTS = 'wellness check-in full results',
  EMPTY_STATE_RESULTS = 'wellness check-in results empty state',
  HOMEBASE_RESULTS = 'wellness check-in homebase results',
}

export enum TRAUMA_CHECKIN_PAGES {
  OVERVIEW = 'trauma check-in overview',
  RESULTS = 'trauma check-in full results',
}

export enum CENTER_OF_EXCELLENCE_PAGES {
  NEURODIVERSITY_CARE_OPTIONS = 'neurodiversity care options sub-page',
  HEALTH_CHALLENGES_CARE_OPTIONS = 'health challenges care options sub-page',
  WOMENS_HEALTH_CARE_OPTIONS = 'womens health care options sub-page',
  HOMEBASE_NEURO_BANNER_ENTRY_POINT = 'homebase neuro banner',
}

export const CENTER_OF_EXCELLENCE_TO_PAGES = {
  [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: {
    toolkit: COMMON_MIXPANEL_PAGE.NEURODIVERSITY_TOOLKIT,
    careOptions: CENTER_OF_EXCELLENCE_PAGES.NEURODIVERSITY_CARE_OPTIONS,
  },
  [CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES]: {
    toolkit: COMMON_MIXPANEL_PAGE.HEALTH_CHALLENGES_TOOLKIT,
    careOptions: CENTER_OF_EXCELLENCE_PAGES.HEALTH_CHALLENGES_CARE_OPTIONS,
  },
  [CENTER_OF_EXCELLENCE.WOMENS_HEALTH]: {
    toolkit: COMMON_MIXPANEL_PAGE.WOMENS_HEALTH_TOOLKIT,
    careOptions: CENTER_OF_EXCELLENCE_PAGES.WOMENS_HEALTH_CARE_OPTIONS,
  },
}

export type PAGES =
  | PROFILE_PAGES
  | REGISTRATION_PAGES
  | COMMON_MIXPANEL_PAGE
  | COMMON_SEARCH_FOR_CARE_SCREENS
  | COMMON_REGISTRATION_SCREENS
  | WELLNESS_CHECK_IN_PAGES
  | TRAUMA_CHECKIN_PAGES
  | COMMON_BCX_SCREENS
  | TRANSFER_ACCOUNT_PAGES
  | CENTER_OF_EXCELLENCE_PAGES

export const createPageViewEventName = (page: PAGES, skipAppendPageToEventName?: boolean) => {
  return `view ${page}${skipAppendPageToEventName ? '' : ' page'}`
}

export const createPageViewEventNameWithoutPageSuffix = (page: PAGES) => {
  return `view ${page}`
}

export enum PROFILE_EVENTS {
  PROFILE_CLICK_NAME_CHANGE = 'Profile - Click name change',
  CHANGE_EMAIL_CONFIRMATION = 'Send Email Change Verification',
  RESEND_CHANGE_EMAIL_CONFIRMATION = 'Resend Email Change Verification',
  CONFIRM_EMAIL_CHANGE = 'Email Change Success',
  ENROLL_MFA = 'Send enroll mfa verification code',
  VERIFY_MFA = 'Enter mfa verification code',
  ENROLLED_MFA = 'Successfully enrolled in mfa',
  DISABLE_MFA = 'Click disable mfa',
  UPDATE_DISPLAY_LANGUAGE = 'Update user display language',
  UPDATE_PHONE_NUMBER = 'Update phone number',
  UPDATE_NOTIFICATION_SETTINGS = 'Update notification settings',
  CANCEL_CUSTOM_NOTIFICATION_SETTINGS = 'Cancel Custom Notification settings',
  DELETE_CUSTOM_NOTIFICATION_SETTINGS = 'Delete Custom Notification settings',
  UPDATE_CUSTOM_NOTIFICATION_SETTINGS = 'Update Custom Notification settings',
  RESEND_MFA_EMAIL = 'Resend mfa email for verification code',
  CHANGE_COUNTRY = 'User account country updated to ',
  CHANGE_TIMEZONE = 'User account timezone updated to ',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_FOR_LIFE_EVENT = 'Locked Profile - Name Change - Click for life event',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_CARE_FOR_CHILD_DEPENDENT = 'Locked Profile - Name Change - Click care for child dependent',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_CARE_FOR_SELF = 'Locked Profile - Name Change - Click care for self',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_CARE_FOR_ADULT_DEPENDENT = 'Locked Profile - Name Change - Click care for adult dependent',
  LOCKED_PROFILE_NAME_CHANGE_CLICK_REASON_NOT_LISTED_ON_PAGE = 'Locked Profile - Name Change - Click reason not listed on page',
  UPDATE_USER_INFO_SUBMITTED = 'Update user info submitted',
  CHANGE_THEME = 'User display theme updated to ',
  UPDATE_CHILD_INFO_SUCCESS = 'Child profile - update child info success',
  UPDATE_CHILD_INFO_FAILED = 'Child profile - update child info failed',
  CLICK_ADD_A_DEPENDENT = 'click add a dependent',
  ADD_PREFERRED_NAME = 'add preferred name',
  DISMISS_BANNER_NOTIFICATION = 'dismiss account page banner',
  CLICK_UPDATE_EMAIL_BANNER = 'click update email account page banner',
}

export enum ADD_DEPENDENT_PROPERTIES {
  DEPENDENT_AGE = 'dependent_age',
  DEPENDENT_LYRA_ID = 'dependent_lyra_id',
  DEPENDENT_TYPE = 'dependent_type',
}

export enum BOOKING_EVENTS {
  BOOKED_LYRA_APPOINTMENT = 'booked lyra treatment',
  BOOKED_EXTERNAL_APPOINTMENT = 'booked external treatment',
  BOOK_FOLLOW_UP_SESSION = 'book follow up session',
}

export enum HEALTHCARE_BOOKING_PROPERTIES {
  HEALTHCARE_SERVICE_NAME = 'healthcare_service_name',
}

export const MIXPANEL_VENDOR_API_URL = `${VENDOR_BASE_API_URL}/mixpanel`

export enum REFERRAL_EVENTS {
  CLICK_REFERRAL_CTA = 'click referral CTA',
  SHARE_REFERRAL_LINK = 'share referral link',
  SHARE_REFERRAL_LINK_CANCELLED = 'cancel share referral link',
  VIEW_COMING_SOON_MICROSITE = 'view coming soon landing page',
}

export enum REFERRAL_ENTRY_POINTS {
  LOGIN_PAGE = 'login page',
  HAMBURGER_MENU = COMMON_ENTRY_POINTS.HAMBURGER_MENU,
  MHAM_MICROSITE = 'mham microsite',
  WELCOME_EMAIL = 'welcome email',
}

export enum ZOOM_EVENTS {
  START_AUDIO = 'start audio',
  START_AUDIO_ERROR = 'start audio error',
  START_AUDIO_SUCCESS = 'start audio success',
  TOGGLE_MUTE = 'toggle mute',
  TOGGLE_MUTE_SUCCESS = 'toggle mute success',
  TOGGLE_MUTE_ERROR = 'toggle mute error',
  TOGGLE_VIDEO = 'toggle video',
  TOGGLE_VIDEO_SUCCESS = 'toggle video success',
  TOGGLE_VIDEO_ERROR = 'toggle video error',
  BAD_CONNECTION = 'bad connection',
  START_SCREENSHARE = 'start screenshare',
  STOP_SCREENSHARE = 'stop screenshare',
  RECEIVE_SCREENSHARE = 'receive screenshare',
  STOP_RECEIVE_SCREENSHARE = 'stop receive screenshare',
  RENDER_SCREENSHARE = 'render screenshare',
  STOP_RENDER_SCREENSHARE = 'stop render screenshare',
  CONNECTION_CONNECTED = 'connection connected',
  CONNECTION_CLOSED = 'connection closed',
}

export enum ZOOM_PROPERTIES {
  STREAM_EXISTS = 'stream_exists',
  MUTED = 'muted',
  VIDEO_OFF = 'video_off',
  ERROR = 'error',
  SESSION_STARTED = 'session_started',
  IS_SELF = 'is_self',
  LEVEL = 'level',
  CONNECTION_TYPE = 'connection_type',
  CONNECTION_CHANGE_REASON = 'connection_change_reason',
}

export enum LYRA_CARE_ENTRY_POINT {
  HEADER = 'header',
  LC_SECTION = 'LC section',
}

export enum COVERAGE_MESSAGING_EVENTS {
  OPEN_COVERAGE_MODAL = 'open coverage modal',
  CLOSE_COVERAGE_MODAL = 'close coverage modal',
}

export enum COVERAGE_MESSAGING_ENTRY_POINT {
  FORKED_TRIAGE = 'Forked Triage',
  MY_BENEFITS = 'My Benefits',
}

export enum PERSONAL_INFO_PROPERTIES {
  IS_PERSONAL_INFO_EDITABLE = 'is_personal_info_editable',
  ARE_FIELDS_EDITABLE = 'are_fields_editable',
}

export enum MINDFUL_MINUTES_EVENTS {
  ENABLE_LOGGING = 'enable mindful minutes logging',
  DISABLE_LOGGING = 'disable mindful minutes logging',
}

export enum QUICK_ACTION_EVENTS {
  CLICK_QUICK_ACTION = 'click quick action',
}

export enum RESCHEDULE_PAGE {
  REBOOK_AFTER_CANCEL = 'Rebook after cancel',
  RESCHEDULE = 'Reschedule',
}

export enum COMMON_TOOLKIT_PROPERTIES {
  TOOLKIT_TOPIC = 'toolkit_topic',
  STATE = 'state',
}

export enum WELLNESS_CHECK_IN_EVENTS {
  ASSESSMENT_COMPLETION = 'wellness check-in assessment completion',
  CLICK_WELLNESS_CHECK_IN_START_AGAIN = 'click wellness check-in start again',
  CLICK_RESULTS_DROP_DOWN_MENU = 'click wellness check-in results drop down menu',
  CLICK_FIND_CARE = 'click find care - wellness check-in',
  CLICK_LEARN_MORE_ABOUT_SOCIAL_CARE = 'click learn more about social care',
  CLICK_EXIT_BUTTON = 'click exit button',
  CLICK_HEADER_LYRA_LOGO = 'click header lyra logo',
  VIEW_WELLNESS_CHECK_IN_ENTRY_POINT = 'view wellness check-in entry point',
  VIEW_WELLNESS_CHECK_IN_CAROUSEL = 'view wellness check-in carousel',
}

export enum TRAUMA_CHECKIN_EVENTS {
  VIEW_OVERVIEW_PAGE = 'view trauma check-in overview page',
  VIEW_FULL_RESULTS_PAGE = 'view trauma check-in full results page',
}

export enum TRAUMA_CHECKIN_PROPERTIES {
  TRAUMA_SCORE = 'trauma_score',
}

export enum TRAUMA_CHECKIN_VERSION {
  PROVIDER_SEARCH = 'provider search',
  ESSENTIALS = 'essentials',
}

export enum WELLNESS_CHECK_IN_ENTRY_POINTS {
  MICROSITE = COMMON_MIXPANEL_PAGE.MICROSITE,
  ASSESSMENT = 'assessment',
  EMAIL = COMMON_ENTRY_POINTS.EMAIL,
  INTEGRATED_BENEFITS_PAGE = COMMON_MIXPANEL_PAGE.INTEGRATED_BENEFITS_PAGE,
  HOMEBASE = COMMON_MIXPANEL_PAGE.HOME_BASE,
  HOMEBASE_PREVIOUS_RESULTS = 'homebase previous results',
  INTEGRATED_BENEFITS_PAGE_PREVIOUS_RESULTS = 'integrated benefits previous results',
  RESULTS = WELLNESS_CHECK_IN_PAGES.RESULTS,
  SMART_SEARCH = COMMON_ENTRY_POINTS.SMART_SEARCH_RESULTS,
}

/** Allow string type to support cms microsites metadata 'page' value as an entry point */
export type WELLNESS_CHECK_IN_ENTRY_POINTS_TYPE = WELLNESS_CHECK_IN_ENTRY_POINTS | string

export enum WELLNESS_CHECK_IN_PROPERTIES {
  ASSESSMENT_RESULTS = 'assessment results',
  PREVIOUS_RESPONSE = 'previous response',
  CAROUSEL_SLIDE_RANK = 'carousel_slide_rank',
  ITEMS_SHOWN = 'items_shown',
  SECTION = 'section',
}

export type WELLNESS_CHECK_IN_CAROUSEL_ITEM = {
  item_type: string
  item_title: string
  destination_slug: string
  item_carousel_category: string
}

export enum URGENCY_TO_BOOK_PROPERTIES {
  URGENCY_TO_BOOK_FLAG = 'urgency_to_book_flag',
  PROVIDER_CAPACITY = 'provider_capacity',
}

export enum COST_DETAILS_EVENTS {
  CLICK_COST_DETAILS_MODAL = 'click cost details modal',
  EXPAND_COST_DETAILS_MODAL = 'expand cost details modal',
  CLICK_EMPLOYER_HEALTH_PLAN_COST_TRANSPARENCY_LINK = 'click employer health plan cost transparency link',
}

export enum AUTO_ENROLLMENT_PATHWAYS_EVENTS {
  CLICK_HEADER_LYRA_LOGO = 'click lyra logo',
  CLICK_HEADER_SKIP = 'click header skip',
  CLICK_THERAPY_COACHING_PATHWAY = 'click therapy and coaching pathway',
  CLICK_MEDITATIONS_COURSES_PATHWAY = 'click meditations and courses pathway',
  CLICK_EXPLORE_ON_YOUR_OWN_PATHWAY = 'click explore on your own pathway',
}

export enum ONE_TIME_PASSCODE_EVENTS {
  EMAIL_VERIFICATION_CODE_INPUT = 'Email verification code input',
  RESEND_EMAIL_VERIFICATION_CODE = 'Resend email verification code',
}

export enum WORK_LIFE_SERVICES_EVENTS {
  VIEW_WORKLIFE = 'View Worklife Services',
  SELECT_WORKLIFE = 'Select Worklife Service',
}

export enum SELF_CARE_MEDIA_EVENTS {
  CLICK_DISCOVER = 'click discover from toolkit',
  AUDIO_PLAY = 'Audio play',
  SHOW_INTERACTIVE_ESSENTIALS_MEDIA = 'show interactive essentials media',
  CLICK_SHOW_TRANSCRIPT = 'click show transcript',
  CLICK_ESSENTIALS_VIDEO = 'click essentials video',
}

export enum SELF_CARE_MEDIA_EVENTS_PROPERTIES {
  DESTINATION_URL = 'destination_url',
  CONTENT_ID = 'content_id',
  CONTENT_FORMAT = 'content_format',
  CONTENT_TYPE = 'content_type',
  LIBRARY_NAME = 'library_name',
  ESSENTIALS = 'essentials',
  DURATION = 'duration',
  URL_SLUG = 'url_slug',
  TITLE = 'title',
  FROM_LYRA = 'from_lyra',
}

export enum COST_TRANSPARENCY_ENTRY_POINTS {
  PROVIDER_PROFILE = 'provider overview',
  PAYMENT_METHOD = 'payment method',
  BOOKING_CONFIRMATION = 'booking confirmation',
}

export const GCT_TREATMENT_OPTION_DETAIL = 'performance coaching'

export const PARENTAL_LEAVE_TREATMENT_OPTION_DETAIL = 'parental leave coaching'

export const MANAGER_COACHING_TREATMENT_OPTION_DETAIL = 'manager coaching'

export const DBT_TREATMENT_OPTION = 'DBT'

export enum MOBILE_ONLY_EVENTS {
  CLICK_PUSH_NOTIFICATION = 'click push notification',
  CLICK_TURN_ON_PUSH_NOTIFICATIONS = 'click turn on push notifications',
  SHOW_PUSH_NOTIFICATION_CONSENT = 'show push notification consent',
  CLICK_ALLOW_PUSH_NOTIFICATIONS = 'click allow push notifications',
}

export enum MOBILE_ONLY_EVENT_PROPERTIES {
  PUSH_NOTIFICATION_TITLE = 'push_notification_title',
  PUSH_NOTIFICATION_BODY = 'push_notification_body',
  PUSH_NOTIFICATION_TEMPLATE_NAME = 'push_notification_template_name',
}

export enum CENTER_OF_EXCELLENCE_PROPERTIES {
  AGE_GROUP = 'age_group',
  MULTIPLE_AGE_GROUPS_AVAILABLE = 'multiple_age_groups_available',
  PROGRAM_DETAIL = 'program_detail',
  TOOLKIT_SUBTOPIC = 'toolkit_subtopic',
  IS_ASD_COORDINATION_ENABLED = 'isASDCoordinationEnabled',
  TOOLKIT_TOPIC = 'toolkit_topic',
  CARE_OPTIONS = 'care_options',
  ORIGINAL_TOOLKIT_TOPIC = 'original_toolkit_topic',
  PAGE_VERSION = 'page_version',
  PAGE_SECTION = 'page_section',
  SECTION_NUMBER = 'section_number',
}

export enum CENTER_OF_EXCELLENCE_EVENTS {
  VIEW_CENTER_OF_EXCELLENCE_TOOLKIT = 'view center of excellence toolkit',
  VIEW_CARE_OPTIONS_SUBPAGE = 'view care options subpage',
  CLICK_CARE_OPTIONS_SUBPAGE_PROGRAM = 'click care options subpage program',
  CLICK_CARE_OPTIONS_SUBPAGE_MODAL_CONTINUE = 'click care options subpage modal continue',
  CLICK_NEURO_BANNER_FROM_HOMEBASE = 'click neuro banner from homebase',
  CLICK_GET_STARTED = 'click get started',
  CLICK_GET_IN_TOUCH = 'click get in touch',
}

export enum NEURODIVERSITY_TOOLKIT_PAGE_VERSION {
  ADULT_CHILD = 'adult_child',
  ADULT_ONLY = 'adult_only',
  TEEN = 'teen',
}

export enum NEURODIVERSITY_TOOLKIT_PAGE_SECTION {
  HERO = 'hero',
  NEEDS = 'needs',
  CARE_OPTIONS = 'care options',
}

export enum CENTER_OF_EXCELLENCE_TOOLKIT_TOPIC {
  HEALTH_CHALLENGES = 'health-challenges',
  WOMENS_HEALTH = 'womens-health',
}

export enum ROI_FORM_EVENTS {
  ROI_FORM_START = 'roi_form_start',
  ROI_SUBMISSION = 'roi_submission',
  ROI_FORM_EDIT = 'roi_form_edit',
  ROI_FORM_CLOSE = 'roi_form_close',
  ROI_FORM_CONTINUATION = 'roi_form_continuation',
  ROI_FORM_BACK = 'roi_form_back',
}

export enum MFA_EVENTS {
  CODE_VERIFICATION_SUBMIT = 'mfa app submit',
  CODE_VERIFICATION_SUCCESS = 'mfa app success',
  CODE_VERIFICATION_FAILED = 'mfa app page code error',
  CLICK_CANNOT_SCAN_QR_CODE = 'mfa app can’t scan qr code',
}

export enum MFA_ENTRY_POINTS {
  REGISTRATION = 'registration',
  LOGIN = 'login',
}

export enum CARE_OPTIONS_CAROUSEL_EVENTS {
  VIEW_CARE_OPTION = 'view',
  CLICK_CARE_OPTION = 'click',
}

export enum CARE_OPTIONS_CAROUSEL_PROP {
  AVAILABLE_PROGRAMS = 'available_programs',
}

export enum CARE_OPTIONS_CAROUSEL_ENTRY_POINT {
  HOMEBASE_CARE_CAROUSEL = 'homebase_care_carousel',
}

export enum AURORA_HOMEBASE_PROP {
  AURORA_FLAG = 'aurora_flag',
  SESSION_LIMIT = 'session_limit',
  SESSION_COVERAGE = 'session_coverage',
  WORK_LIFE_SERVICE = 'is_work_life_service_available',
}

export enum RESOURCE_GRID_EVENT {
  VIEW_ESSENTIALS_RESOURCE_GRID = 'view essentials resource grid',
}

export enum RESOURCE_GRID_PROP {
  EVENTS = 'events_in_carousel',
  ITEMS_SHOWN = 'items_shown',
}

export enum SESSION_COST_ESTIMATE_PROPERTIES {
  COST_ESTIMATES_SHOWN = 'cost_estimates_shown',
}

export enum SESSION_COST_ESTIMATE_EVENTS {
  CLICK_COST_ESTIMATE = 'click cost estimate on upcoming session',
  COST_ESTIMATES_SHOWN = 'cost estimates shown',
}

export enum DBT_HOMEBASE_EVENTS {
  VIEW_CARE_OPTION_NEXT_UP_DBT_STATUS = 'view care option - next up dbt status',
  CLICK_NEXT_UP_DBT = 'click next up dbt',
}

export enum DBT_HOMEBASE_PROPERTIES {
  ELIGIBILITY_STATUS = 'eligibility_status',
  CTA_TYPE = 'cta_type',
}

export enum DBT_HOMEBASE_ELIGIBILITY_STATUS {
  ELIGIBLE = 'eligible',
  INELIGIBLE_SELF_PAY = 'ineligible_self_pay',
}

export enum DBT_HOMEBASE_CTA_TYPE {
  EXTERNAL_FORM = 'external_form',
  UPDATE_HP_INFO = 'update_hp_info',
}

export enum ESSENTIALS_SEARCH_EVENTS {
  SEARCH_ESSENTIALS = 'Search Essentials',
  CLICK_ESSENTIALS_SEARCH_RESULT = 'Click Essentials Search Result',
}

export enum ESSENTIALS_SEARCH_PROPERTIES {
  ESSENTIALS_LIBRARY_SEARCH_QUERY_ID = 'essentials_library_search_query_id',
}
